export type Language = 'es' | 'gl' | 'en';

export const translations = {
  es: {
    // Header
    nav: {
      whatHappens: "Reto",
      ourSolution: "Producto",
      meetUs: "Conózcanos",
      join: "Contacto",
      login: "Iniciar sesión",
      startFree: "Empiece gratis",
      news: "Noticias",
    },

    // Hero Section
    hero: {
      badge: "Finalistas del Programa de Innovación Bankinter",
      badgeLine1: "Finalistas del Programa",
      badgeLine2: "de Innovación Bankinter",
      title: "Reduzca fugas.",
      titleHighlight: "Digitalice su gestión del agua.",
      subtitle: "El software de gestión para ayuntamientos que predice dónde actuar frente a fugas de agua, monitoriza consumos y conecta a los ciudadanos. Adiós al caos. Hola a EGS.",
      ctaPrimary: "Empiece gratis",
      newsTicker: "Mención en los Premios AJE Pontevedra 2026",
      ctaSecondary: "Nuestro equipo",
    },

    // Logo Wall
    logos: {
      heading: "Originado en",
    },

    // Global Water Crisis Section
    crisis: {
      title: "EGS responde a la crisis global del agua",
      subtitle: "Existen datos alarmantes que demuestran la urgencia de actuar ahora:",
      stat1: "del agua de la Tierra es apta para el consumo humano",
      stat2: "de personas podrían carecer de agua potable para 2050",
      stat3: "gasta una persona al día. Reduciendo a 50 L, se rompería el círculo de pobreza",
      trendsTitle: "Dos tendencias agravan la crisis hídrica",
      trend1Title: "Crecimiento poblacional",
      trend1Text: "La población mundial alcanzará los 9.700 millones de personas para 2050. El aumento de la población conduce a una mayor demanda de agua potable. Según la ONU, 1.800 millones de personas vivirán en zonas con falta de agua potable.",
      trend2Title: "Cambio climático",
      trend2Text: "El aumento de temperaturas y la alteración de patrones de lluvia reducen drásticamente la disponibilidad de agua dulce. Según la OMS, un 20 % de la población mundial carece de acceso regular a agua potable y saneamiento, provocando la proliferación de enfermedades.",
    },

    // Galicia Section
    galicia: {
      title: "Galicia no es una excepción",
      stat1: "Comunidad que más agua pierde por fugas en su red, según S&P Global Ratings",
      stat2: "de litros diarios son recuperables con reparación de fugas",
      stat3Only: "solo",
      stat3: "de municipios previenen las fugas pese al riesgo de sequía",
      closing: "Galicia y Asturias son las comunidades con mayores fugas de agua en sus redes. Murcia, Andalucía y Baleares enfrentan el mayor estrés hídrico.",
      closingHighlight1: "Galicia y Asturias",
      closingHighlight2: "Murcia, Andalucía y Baleares",
    },

    // Solution Section
    solution: {
      badge: "Para ayuntamientos e instituciones",
      title: "Nuestra solución",
      subtitle: "Sistema integral para la gestión del ciclo del agua",
      description: "Ofrecemos un sistema de gestión web que parte de ficheros de registros del agua para aportar datos centralizados, otorgar control total y favorecer decisiones más inteligentes a las instituciones.",
      comingSoon: "Próximamente",
      comingSoonTitle: "Tecnología al servicio del agua",
      comingSoonSubtitle: "Predicciones, alertas y métricas digitalizadas para reducir las fugas y el desperdicio hídrico",
      scrollTitle: "Disponga de un sistema integral para la",
      scrollSubtitle: "Gestión del Agua",
      systemIntro: "Controlamos y reducimos el consumo de agua no registrado mediante la detección de fugas y patrones de gasto. El sistema permite a",
      systemTarget: "ayuntamientos e instituciones",
      systemAllows: ":",
      feature1Bullet: "Obtener un <strong>mapa priorizado</strong> de <strong>puntos de actuación</strong>, mediante predicciones y alertas digitalizadas",
      feature2Bullet: "Calcular sobrecostes estimados de las pérdidas",
      feature3Bullet: "Reportar lecturas de contadores e incidencias de los ciudadanos",
      description2: "Dividimos el servicio en 3 capas principales",
      stepsTitle: "Cómo funciona EGS",
      feature1Title: "IA predictiva",
      feature1Text: "Algoritmos capaces de anticiparse a los problemas, prediciendo y alertando antes de que ocurran para optimizar el mantenimiento de los sistemas hídricos.",
      feature2Title: "Análisis avanzado",
      feature2Text: "Dashboards en tiempo real que analizan patrones de consumo, predicen la demanda de recursos y estiman costes asociados de forma detallada.",
      feature3Title: "Gestión completa",
      feature3Text: "Propuesta de soluciones sostenibles frente a las fugas o vulnerabilidades detectadas, garantizando una gestión del agua más eficiente y responsable.",
      videoHeadline1: "Control total para su municipio",
      videoHeadline2: "El agua, en manos del ciudadano",
      videoPill1_1: "Mapa de fugas",
      videoPill1_2: "Alertas predictivas",
      videoPill1_3: "Gestión técnica",
      videoPill2_1: "Consumo en tiempo real",
      videoPill2_2: "Reporte de incidencias",
    },

    // About Us Section
    about: {
      title: "Conózcanos",
      intro: "En EGS, somos un equipo multidisciplinar de emprendedores gallegos con una misión clara: transformar la gestión del agua a nivel global, comenzando desde nuestras ciudades, villas y pueblos, pero con la ambición de marcar la diferencia a gran escala.",
      missionTitle: "Nuestra misión",
      missionText: "Garantizar acceso universal a agua limpia mediante tecnología con propósito y gestión inteligente.",
      purposeTitle: "Nuestro propósito",
      purposeText: "Crear un movimiento global que transforme la gestión del agua para las generaciones futuras.",
      keyTitle: "Nuestra clave",
      keyText: "Para lograr un impacto duradero, combinamos colaboración con conocimiento compartido.",
      teamIntro: "Una organización se construye a partir de sus personas. Detrás de EGS hay un equipo diverso y apasionado por lo que hace y el impacto que busca generar.",
      luis: {
        name: "Luis Encinas",
        role: "Relaciones Institucionales y Finanzas",
        bio: "Emprendedor con firme compromiso social y visión transformadora del impacto tecnológico.",
      },
      ignacio: {
        name: "Ignacio Garbayo",
        role: "Desarrollo e Inteligencia Artificial",
        bio: "Da vida a lo que desarrolla con la convicción de que la tecnología transformará la gestión del agua.",
      },
      martin: {
        name: "Martín Sende",
        role: "Soluciones de Gestión y Sostenibilidad",
        bio: "Apasionado por la ciencia y convencido del impacto real de la tecnología sobre los recursos naturales.",
      },
      viewProfile: "Ver perfil",
    },

    // FAQ Section
    faq: {
      title: "Preguntas frecuentes",
      subtitle: "Resolvemos las dudas más habituales sobre EGS y cómo trabajamos con los ayuntamientos.",
      items: [
        {
          question: "¿Necesitamos instalar sensores o cambiar los contadores?",
          answer: "No. EGS funciona con las lecturas de contadores que el ayuntamiento ya recoge; no hace falta instalar hardware ni sustituir nada para empezar. Adaptamos el funcionamiento a su modelo.",
        },
        {
          question: "¿De quién son los datos y cómo se protege la privacidad?",
          answer: "Los datos son siempre del ayuntamiento; EGS solo los procesa para prestar el servicio, conforme al RGPD y alojados en la UE.",
        },
        {
          question: "¿En qué consiste el piloto?",
          answer: "Es gratuito. El ayuntamiento solo aporta un histórico de lecturas; al terminar recibe un mapa priorizado de posibles fugas, sin compromiso.",
        },
        {
          question: "¿Cómo se contrata desde una administración pública?",
          answer: "Tras el piloto, EGS puede contratarse por contrato menor, suscripción o integrarse en un pliego, con acompañamiento durante todo el proceso.",
        },
        {
          question: "¿Quién está detrás de EGS?",
          answer: "Un equipo gallego surgido en la USC, respaldado por la Fundación Innovación Bankinter y la Fundación Princesa de Girona.",
        },
      ],
      knowUsLinkIntro: "Más en la sección",
      knowUsLinkText: "Conózcanos",
      linkedinLinkIntro: "o en nuestro",
      linkedinLinkText: "LinkedIn",
    },

    // Join Section
    join: {
      title: "Únete",
      subtitle: "Sé parte del cambio. Buscamos instituciones y colaboradores que busquen transformar la gestión del agua y crear un futuro más sostenible para las próximas generaciones.",
      whyTitle: "Empiece ahora con EGS",
      impactTitle: "Impacto",
      impactText: "Detecte fugas antes de que se conviertan en averías. Menos coste, más agua aprovechada.",
      commitmentTitle: "Compromiso",
      commitmentText: "Nos integramos con su infraestructura actual. Sin obras, sin hardware adicional.",
      techTitle: "Tecnología",
      techText: "Dashboards en tiempo real con alertas automáticas. Gestione toda la red desde una sola pantalla.",
      contactTitle: "Contacte directamente",
      formTitle: "Comience su aventura",
      formSubtitle: "Nos pondremos en contacto con usted lo antes posible.",
      formName: "Nombre",
      formLastName: "Apellidos",
      formEmail: "Correo electrónico",
      formCollaboration: "Cargo en la organización",
      formCollaborationPlaceholder: "Selecciona una opción",
      formCollaborationOptions: {
        partner: "Técnico/a de Aguas",
        client: "Técnico/a de Infraestructuras",
        investor: "Responsable de Servicios Municipales",
        talent: "Alcalde/Concejal/Funcionario",
        other: "Otro cargo",
      },
      formMessage: "Cuéntenos sobre su municipio",
      formMessagePlaceholder: "Organización o concello, población aproximada (hab.), teléfono de contacto y situación actual de la red...",
      formPrivacy: "Acepto la",
      formPrivacyLink: "política de privacidad",
      formSubmit: "Solicitar piloto",
      formSubmitting: "Enviando...",
      formSuccessMessage: "¡Gracias! Hemos recibido tu solicitud. Te contactaremos pronto.",
      formErrorPrivacy: "Debes aceptar la política de privacidad",
      formErrorRequired: "Por favor completa todos los campos obligatorios",
      formErrorGeneral: "Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.",
    },

    // Success Modal
    successModal: {
      title: "Solicitud recibida",
      message: "Hemos registrado tu solicitud correctamente. Nuestro equipo se pondrá en contacto contigo en un plazo de 24 a 48 horas.",
      button: "Cerrar",
    },

    loadingModal: {
      message: "Por favor, espera...",
      videoLoading: "Cargando..."
    },

    // Welcome Email
    welcomeEmail: {
      subject: "Bienvenido a EGS - Confirmación de registro",
      greeting: "Hola,",
      title: "Bienvenido a EGS",
      message: "Gracias por registrarte en nuestra plataforma de gestión inteligente del agua.",
      received: "Hemos recibido tus datos correctamente y están siendo procesados.",
      pending: "Tu solicitud está pendiente de validación por parte de tu institución.",
      nextSteps: "Qué sigue:",
      step1: "Nuestro equipo revisará tu información",
      step2: "Tu institución validará tu acceso",
      step3: "Recibirás un correo cuando tu cuenta esté activa",
      timeframe: "Este proceso suele completarse en 24-48 horas.",
      questions: "¿Tienes alguna duda?",
      contactUs: "No dudes en contactarnos en",
      thanks: "Gracias por unirte a la revolución del agua",
      footer: "EGS",
      footerTagline: "Desde Galicia, transformamos la gestión del agua.",
    },

    // Password Change Page
    passwordChange: {
      title: "Cambiar contraseña",
      subtitle: "Configura tu nueva contraseña de acceso",
      newPassword: "Nueva contraseña",
      confirmPassword: "Confirmar contraseña",
      submit: "Cambiar contraseña",
      success: "Contraseña cambiada correctamente. Redirigiendo al inicio de sesión...",
      errorMinLength: "La contraseña debe tener al menos 8 caracteres",
      errorMismatch: "Las contraseñas no coinciden",
      errorInvalidToken: "El enlace es inválido o ha expirado",
      errorAlreadyUsed: "Este enlace ya ha sido utilizado",
      errorGeneral: "Error al cambiar la contraseña. Inténtalo de nuevo.",
      loading: "Validando enlace...",
    },

    // Credentials Email
    credentialsEmail: {
      subject: "Tu cuenta en EGS ha sido creada",
      title: "Cuenta creada",
      greeting: "Hola",
      message: "Tu cuenta en la plataforma EGS ha sido creada por tu ayuntamiento.",
      instructions: "Para comenzar a usar tu cuenta, necesitas establecer tu contraseña.",
      buttonText: "Cambiar mi contraseña",
      note: "Este enlace es válido por 24 horas.",
      footer: "Si no solicitaste esta cuenta, contacta con tu ayuntamiento.",
      privacyNotice: "Tus datos personales son tratados por EGS conforme al Reglamento General de Protección de Datos (RGPD). Puedes consultar nuestra Política de Privacidad en: ",
    },

    // Footer
    footer: {
      description: "Desde Galicia, transformamos la gestión del agua a través de inteligencia artificial y automatización de procesos para crear un futuro más sostenible.",
      backedBy: "Respaldados por",
      contact: "Contacto",
      quickLinks: "Enlaces rápidos",
      home: "Inicio",
      copyright: "© 2026 EGS. Todos los derechos reservados.",
      privacy: "Política de privacidad",
      legal: "Aviso legal",
      cookies: "Política de cookies",
    },

    // Noticias (posts de LinkedIn) — /{lang}/news
    news: {
      metaTitle: "Noticias | EGS",
      metaDescription: "Las últimas publicaciones de EGS en LinkedIn: agua, concellos y tecnología en Galicia.",
      eyebrow: "Desde nuestra página de LinkedIn",
      title: "Noticias",
      subtitle: "Lo que contamos sobre agua, concellos y tecnología en Galicia. Cada publicación enlaza al post original.",
      originalLanguage: "",
      follow: "Seguir a EGS en LinkedIn",
      featured: "Destacado",
      readPost: "Leer publicación",
      allPosts: "Todas las publicaciones",
      sortedNote: "De la más reciente a la más antigua",
      readMore: "Leer más",
      showMore: "Ver más publicaciones",
      empty: "Todavía no hay publicaciones. Mientras tanto, puede seguirnos en LinkedIn.",
      ctaTitle: "No se pierda lo próximo",
      ctaText: "Síganos en LinkedIn para ver cada novedad el día que la publicamos.",
      ctaButton: "Seguir en LinkedIn",
      back: "Volver a noticias",
      publishedOn: "Publicado en LinkedIn",
      reactions: "reacciones",
      comments: "comentarios",
      viewOnLinkedIn: "Ver en LinkedIn",
      copyLink: "Copiar enlace",
      linkCopied: "Enlace copiado",
      morePosts: "Más publicaciones",
      seeAll: "Ver todas",
      image: "Imagen",
      shared: "Compartido",
      sharedBy: "Compartido por EGS",
      sharedFrom: "Publicación original de",
      linkedinPost: "Publicación de LinkedIn",
    },

    // Login Page
    loginPage: {
      title: "Inicia sesión",
      subtitle: "Accede a tu cuenta de EGS",
      welcomeBack: "Bienvenido de vuelta",
      welcomeDescription: "Entra para acceder a tu panel de control",
      email: "Correo electrónico",
      emailPlaceholder: "tu@email.com",
      password: "Contraseña",
      passwordPlaceholder: "••••••••",
      loginButton: "Iniciar sesión",
      loggingIn: "Iniciando sesión...",
      errorCredentials: "Credenciales incorrectas",
      errorGeneral: "Error al iniciar sesión",
      errorAccountNotValidated: "Tu cuenta aún no ha sido validada por el administrador. Por favor, espera a que se apruebe tu registro.",
      noAccount: "¿No tienes una cuenta?",
      signUpLink: "Regístrate",
      orDivider: "o",
      googleLogin: "Continuar con Google",
      googleLoggingIn: "Conectando con Google...",
      errorGoogle: "Error al iniciar sesión con Google",
    },

    // Sign Up Page
    signupPage: {
      title: "Regístrate",
      subtitle: "Crea tu cuenta de EGS",
      welcome: "Únete a EGS",
      welcomeDescription: "Completa el formulario para solicitar acceso",
      dni: "DNI",
      dniPlaceholder: "12345678A",
      nombre: "Nombre",
      nombrePlaceholder: "Tu nombre",
      apellidos: "Apellidos",
      apellidosPlaceholder: "Tus apellidos",
      email: "Correo electrónico",
      emailPlaceholder: "tu@email.com",
      password: "Contraseña",
      passwordPlaceholder: "••••••••",
      confirmPassword: "Repetir contraseña",
      confirmPasswordPlaceholder: "••••••••",
      telefono: "Teléfono (opcional)",
      telefonoPlaceholder: "612345678",
      concello: "Concello",
      concelloPlaceholder: "Selecciona tu concello",
      notifications: "Recibir notificaciones por correo",
      notificationsHelper: "Solo para alertas sobre tus viviendas. Los correos informativos de tu cuenta se enviarán automáticamente.",
      signupButton: "Registrarse",
      signingUp: "Registrando...",
      haveAccount: "¿Ya tienes cuenta?",
      loginLink: "Inicia sesión",
      errorDniFormat: "Formato de DNI inválido (8 dígitos + letra)",
      errorDniRequired: "El DNI es obligatorio",
      errorNombreRequired: "El nombre es obligatorio",
      errorApellidosRequired: "Los apellidos son obligatorios",
      errorEmailFormat: "Formato de correo electrónico inválido",
      errorEmailRequired: "El correo electrónico es obligatorio",
      errorPasswordRequired: "La contraseña es obligatoria",
      errorPasswordLength: "La contraseña debe tener al menos 8 caracteres",
      errorPasswordMismatch: "Las contraseñas no coinciden",
      errorConcelloRequired: "Debes seleccionar un concello",
      errorGeneral: "Error al registrarse. Por favor, intenta de nuevo.",
      errorLoadingConcellos: "Error al cargar los concellos",
      googleSignup: "Registrarse con Google",
      googleSignupDescription: "Tu nombre y email se rellenarán automáticamente desde tu cuenta de Google. Deberás completar el resto de datos.",
      orDivider: "o",
      privacyCheckbox: "He leído y acepto la ",
      privacyLink: "Política de Privacidad",
      errorPrivacyRequired: "Debes aceptar la política de privacidad para registrarte",
    },

    // Sign Up Success Page
    signupSuccess: {
      title: "Solicitud enviada",
      subtitle: "Tu registro está pendiente de aprobación",
      message: "Tu solicitud ha sido enviada a tu institución",
      description: "Recibirás una notificación por correo electrónico cuando tu perfil sea aprobado y puedas acceder a la plataforma.",
      emailNote: "Nota importante",
      emailNoteText: "El permiso de notificaciones que otorgaste durante el registro se refiere únicamente a alertas sobre tus viviendas. Los correos informativos relacionados con tu cuenta se enviarán automáticamente al darte de alta.",
      spamNote: "¿No ves el correo?",
      spamNoteText: "Si no encuentras el correo de bienvenida en tu bandeja de entrada, revisa tu carpeta de spam o promociones. Por favor, marca nuestros correos como 'No es spam' para recibir futuras notificaciones.",
      backToHome: "Volver al inicio",
      goToLogin: "Ir a iniciar sesión",
    },

    // Legal Pages
    legal: {
      privacy: {
        title: "Política de privacidad",
        subtitle: "Información sobre cómo EGS recopila, usa y protege tus datos personales",
        intro: "En <strong>EGS</strong> valoramos la privacidad de nuestros usuarios, propietarios, ayuntamientos y de todas las personas cuyos datos tratamos. Cumplimos con el Reglamento (UE) 2016/679 (RGPD) y con la Ley Orgánica 3/2018, de Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD).",
        section1Title: "1. Responsable del tratamiento",
        section1Content: ["Denominación: EGS (constitución societaria en proceso)", "Domicilio: Galicia, España (dirección definitiva pendiente de registro)", "Contacto: info@egswater.com"],
        section2Title: "2. Datos que tratamos",
        section2Content: "Tratamos datos como nombre, apellidos, dirección, datos de identificación y contacto, datos de vivienda, catastrales, consumos de agua, datos del censo y de propietarios, entre otros. Parte de estos datos son aportados directamente por los ayuntamientos con quienes EGS tiene relación contractual; en esos casos EGS actúa como encargado del tratamiento conforme al Art. 28 RGPD.",
        section3Title: "3. Finalidades y base legal",
        section3Content: [
          { purpose: "Gestión de relaciones con ayuntamientos y propietarios", basis: "Art. 6.1.b RGPD — ejecución de contrato" },
          { purpose: "Estudios, auditorías y servicios sobre consumo de agua", basis: "Art. 6.1.b / Art. 6.1.e RGPD — ejecución de contrato / interés público" },
          { purpose: "Mantenimiento del censo de viviendas", basis: "Art. 6.1.c / Art. 6.1.e RGPD — obligación legal / interés público" },
          { purpose: "Gestión de facturación y cobros", basis: "Art. 6.1.b RGPD — ejecución de contrato" },
          { purpose: "Cumplimiento de obligaciones legales y administrativas", basis: "Art. 6.1.c RGPD — obligación legal" },
          { purpose: "Notificaciones y comunicaciones al usuario (con su consentimiento)", basis: "Art. 6.1.a RGPD — consentimiento del interesado" },
        ],
        section4Title: "4. Conservación de los datos",
        section4Content: "Los datos se conservarán durante los siguientes plazos: datos de cuenta de usuario, 3 años desde la baja; datos de consumo y auditoría, 5 años desde la finalización del contrato con el ayuntamiento; datos de facturación, 6 años conforme al artículo 30 del Código de Comercio. En caso de litigio o reclamación, los datos se conservarán hasta la resolución firme.",
        section5Title: "5. Destinatarios",
        section5Content: "Los datos podrán ser comunicados a ayuntamientos, entidades colaboradoras, proveedores de servicios (encargados del tratamiento) y autoridades competentes, siempre garantizando el cumplimiento de la normativa.",
        section6Title: "6. Transferencias internacionales",
        section6Content: "EGS trabaja con proveedores de servicios tecnológicos (infraestructura cloud, envío de email) que pueden operar parcialmente fuera del Espacio Económico Europeo. En tales casos, EGS exige a dichos proveedores la suscripción de Cláusulas Contractuales Tipo (CCT) aprobadas por la Comisión Europea, u otras garantías adecuadas conforme al Capítulo V del RGPD.",
        section7Title: "7. Derechos del interesado",
        section7Intro: "El usuario puede ejercer los siguientes derechos:",
        section7Content: [
          "Acceder a sus datos",
          "Rectificarlos o suprimirlos",
          "Limitar su tratamiento",
          "Oponerse al tratamiento",
          "Solicitar la portabilidad",
          "Retirar su consentimiento en cualquier momento",
          "Presentar reclamación ante la Agencia Española de Protección de Datos (AEPD)"
        ],
        section7LinkText: "Agencia Española de Protección de Datos (AEPD)",
        section7Exercise: "Para ejercer cualquiera de estos derechos, envía un correo a info@egswater.com indicando el derecho que deseas ejercer y adjuntando copia de tu documento de identidad. EGS responderá en el plazo máximo de 1 mes, prorrogable otros 2 meses en casos de especial complejidad (Art. 12.3 RGPD).",
        section8Title: "8. Tratamiento automatizado e inteligencia artificial",
        section8Content: "EGS utiliza algoritmos de análisis predictivo para la detección de fugas y optimización del consumo de agua. Estos análisis se aplican sobre datos agregados de consumo y no producen decisiones individuales con efectos jurídicos significativos sobre las personas. No se toman decisiones automatizadas en los términos del Art. 22 RGPD.",
        section9Title: "9. Delegado de Protección de Datos",
        section9Content: "EGS ha evaluado la obligatoriedad de designar un Delegado de Protección de Datos (DPD) conforme al Art. 37 RGPD y concluye que, en su fase operativa actual, no concurren los supuestos que lo hacen preceptivo. Esta evaluación será revisada periódicamente conforme crezca el volumen y tipología de datos tratados.",
        section10Title: "10. Menores de edad",
        section10Content: "El servicio de EGS está dirigido exclusivamente a personas mayores de 14 años, conforme al Art. 7 de la LOPDGDD. EGS no recaba conscientemente datos de menores de dicha edad. Si se detecta que un usuario registrado es menor de 14 años, sus datos serán eliminados de forma inmediata.",
        section11Title: "11. Medidas de seguridad",
        section11Content: "Hemos adoptado medidas técnicas y organizativas para proteger los datos frente a accesos no autorizados, pérdidas, alteraciones o divulgaciones indebidas, incluyendo control de accesos, autenticación segura, cifrado y políticas internas de seguridad.",
        section12Title: "12. Brechas de seguridad",
        section12Content: "En caso de una brecha de seguridad que afecte a datos personales, notificaremos a la AEPD y, si procede, también a los afectados, lo antes posible.",
        section13Title: "13. Política de cookies",
        section13Content: "El uso de cookies y tecnologías de rastreo en este sitio web se detalla en nuestra Política de Cookies, disponible en el pie de página.",
        section14Title: "14. Modificaciones",
        section14Content: "Esta política puede actualizarse para adaptarse a cambios legales o internos. En caso de cambios significativos, se informará a través de la web u otros medios adecuados.",
        lastUpdated: "Última actualización: marzo de 2026",
      },
      cookies: {
        title: "Política de cookies",
        subtitle: "Información sobre el uso de cookies en este sitio web",
        intro: "Este sitio web utiliza <strong>cookies</strong> para mejorar la experiencia del usuario, analizar el tráfico y personalizar el contenido de acuerdo a tus preferencias. A continuación, te explicamos de forma detallada qué cookies utilizamos, con qué finalidad y cómo puedes gestionarlas.",
        section1Title: "¿Qué son las cookies?",
        section1Content: [
          "Las cookies son pequeños archivos de texto que los sitios web almacenan en tu navegador cuando los visitas. Sirven para recordar tus preferencias, facilitar la navegación y recopilar información estadística sobre el uso del sitio."
        ],
        section2Title: "Tipos de cookies que utilizamos",
        section2Content: "En función de su finalidad, clasificamos las cookies de este sitio en las siguientes categorías:",
        section3Title: "Cookies estrictamente necesarias",
        section3Content: [
          "Estas cookies son imprescindibles para el funcionamiento básico del sitio web y no se pueden desactivar. Se utilizan para gestionar la autenticación del usuario, mantener la sesión activa y recordar las preferencias de idioma.",
          "<strong>access_token / refresh_token:</strong> Gestionan la sesión del usuario autenticado. Se generan al iniciar sesión y son necesarias para acceder a las áreas protegidas (panel de control). Duración: sesión (se eliminan al cerrar sesión o expirar el token).",
          "<strong>lang</strong> (cookie, 12 meses): Recuerda el idioma seleccionado por el usuario para ofrecer una experiencia coherente entre visitas.",
          "<strong>cookieConsent</strong> (cookie, 12 meses): Guarda tu elección sobre la aceptación o rechazo de cookies. Expira automáticamente tras 12 meses, momento en el que se te volverá a solicitar tu preferencia."
        ],
        section4Title: "Cookies analíticas y de rendimiento",
        section4Content: [
          "Estas cookies nos permiten analizar cómo los usuarios interactúan con el sitio web, ayudándonos a mejorar su funcionamiento y contenido.",
          "<strong>Vercel Analytics:</strong> Utilizamos el servicio de analítica de Vercel, que recopila datos de rendimiento de forma anónima y agregada (tiempos de carga, rutas visitadas). No utiliza cookies persistentes ni identifica a usuarios individuales."
        ],
        section5Title: "Cookies de terceros",
        section5Content: [
          "Algunos servicios integrados en nuestro sitio pueden establecer sus propias cookies:",
          "<strong>Google reCAPTCHA v3:</strong> Utilizado en los formularios de registro para verificar que el usuario es humano. Establece la cookie <strong>_GRECAPTCHA</strong> bajo el dominio <em>.google.com</em> (duración: sesión, hasta 6 meses). Estas cookies son gestionadas por Google y están sujetas a su <a href='https://policies.google.com/privacy' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>política de privacidad</a>.",
          "<strong>Supabase:</strong> Nuestro proveedor de base de datos y autenticación puede generar identificadores técnicos necesarios para la comunicación con el servidor."
        ],
        section6Title: "Gestión de cookies",
        section6Content: "Puedes configurar tu navegador para <span>aceptar</span>, <span>bloquear</span> o <span>eliminar</span> cookies en cualquier momento. Asimismo, puedes retirar tu consentimiento borrando la cookie <strong>cookieConsent</strong> de tu navegador, lo que hará que se te solicite de nuevo tu preferencia en la próxima visita. Ten en cuenta que, si bloqueas algunas cookies, ciertas funciones del sitio pueden no estar disponibles. A continuación, te indicamos cómo gestionar las cookies en los navegadores más habituales:",
        section6Links: [
          "<a href='https://support.google.com/chrome/answer/95647' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>Google Chrome</a>",
          "<a href='https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>Mozilla Firefox</a>",
          "<a href='https://support.apple.com/es-es/guide/safari/sfri11471/mac' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>Safari</a>",
          "<a href='https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>Microsoft Edge</a>"
        ],
        section7Title: "Actualización de esta política",
        section7Content: "Esta política de cookies puede actualizarse periódicamente para reflejar cambios en los servicios utilizados o en la normativa aplicable. Te recomendamos revisarla de forma periódica. La fecha de la última actualización se indica al final de esta página.",
        lastUpdated: "Última actualización: marzo de 2026",
      },
      avisoLegal: {
        title: "Aviso legal",
        subtitle: "Información legal sobre el uso de este sitio web y sus contenidos",
        intro: "En cumplimiento del artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se pone a disposición de los usuarios la siguiente información sobre el titular de este sitio web.",
        section0Title: "Datos del titular del sitio web",
        section0: [
          "Denominación: EGS (constitución societaria en proceso)",
          "NIF/CIF: pendiente de asignación registral",
          "Domicilio: Santiago de Compostela, Galicia, España",
          "Correo electrónico: info@egswater.com",
          "Sitio web: https://egswater.com",
        ],
        section1Title: "Propiedad intelectual e industrial",
        section1Content: "Todos los contenidos, logos, imágenes y textos publicados en este sitio web son propiedad de <strong>EGS</strong> o cuenta con la licencia correspondiente para su uso. Queda prohibida su reproducción total o parcial, distribución, transformación o comunicación pública sin autorización expresa y por escrito de EGS. Las marcas, nombres comerciales o signos distintivos son titularidad de EGS o de terceros que han autorizado su uso.",
        section2Title: "Responsabilidad",
        section2Content: "EGS no se hace responsable de errores, omisiones o daños derivados del uso de la información proporcionada en este sitio o empleada para otros fines distintos a los planteados. Asimismo, EGS no se responsabiliza de los contenidos de sitios web externos enlazados desde este portal, cuya gestión corresponde a sus respectivos titulares. EGS no garantiza la disponibilidad ininterrumpida del sitio web y se reserva el derecho a suspender o interrumpir el acceso al mismo por razones técnicas o de mantenimiento.",
        section3Title: "Legislación aplicable y jurisdicción",
        section3Content: "Las presentes condiciones de uso se rigen por la legislación española, en particular por la LSSI-CE y demás normativa aplicable. Para la resolución de cualquier controversia derivada del uso de este sitio web, las partes se someten a los juzgados y tribunales de Santiago de Compostela, con renuncia expresa a cualquier otro fuero que pudiera corresponderles.",
        section4Title: "Modificaciones del aviso legal",
        section4Content: "EGS se reserva el derecho a modificar el presente aviso legal en cualquier momento. Los cambios serán publicados en esta misma página. Se recomienda consultar este aviso periódicamente para mantenerse informado de posibles actualizaciones.",
        lastUpdated: "Última actualización: marzo de 2026",
      },
    },
    dashboard: {
      pageTitle: "Panel institucional | EGS",
      session: {
        loading: "Cargando",
        notStarted: "Sesión no iniciada",
        notStartedDesc: "No has iniciado sesión o tu sesión ha expirado.",
        backToLogin: "Volver a Iniciar Sesión",
      },
      notifications: {
        types: {
          newFile: "Nuevo fichero subido",
          analysisComplete: "Análisis completado",
          filePersisted: "Fichero persistido",
          analysisError: "Error en análisis",
          readingsParsed: "Parseo de lecturas completado",
          scoresCalculated: "Scores calculados",
          readingsError: "Error en parseo de lecturas",
          scoresError: "Error en scores",
        },
        loading: "Cargando...",
        empty: "Sin notificaciones",
      },
      menu: {
        resumen: "Resumen",
        cargar: "Cargar datos",
        mapa: "Predicciones de fuga",
        mapa2: "Consumo excesivo",
        riesgos: "Riesgos",
        solicitudes: "Solicitudes",
        incidencias: "Incidencias",
        lecturas: "Lecturas",
        administracion: "Administración",
        informes: "Informes",
        configuracion: "Configuración",
      },
      sidebar: {
        title: "Panel institucional",
        logout: "Cerrar sesión",
        logoutDialogTitle: "Cerrar sesión",
        logoutDialogDesc: "¿Estás seguro de que quieres cerrar sesión?",
        logoutCancel: "Cancelar",
        logoutConfirm: "Cerrar sesión",
      },
      severity: {
        ok: "OK",
        watch: "Vigilar",
        urgent: "Inspección urgente",
        estimated: "estimación",
      },
      sections: {
        riesgos: {
          title: "Riesgos",
          subtitle: "Fugas y consumo excesivo por vivienda",
          tabLeaks: "Fugas",
          tabConsumption: "Consumo excesivo",
          seccionLabel: "Sección",
          periodoLabel: "Período",
          selectSeccion: "Selecciona una sección",
          selectPeriodo: "Selecciona un período",
          mapTitle: "Mapa de riesgo",
          togglePoints: "Puntos",
          toggleHeatmap: "Mapa de calor",
          panelTitle: "Detalle de vivienda",
          panelEmpty: "Selecciona una vivienda en el mapa o la tabla",
          impactLabel: "Impacto estimado",
          searchHousing: "Buscar vivienda...",
          colHousing: "Vivienda",
          colPeriod: "Periodo",
          colProb: "Prob.",
          colImpact: "Impacto",
          colSeverity: "Estado",
          colCost: "Coste (est.)",
          pagPrev: "Anterior",
          pagNext: "Siguiente",
          pagShowing: "Mostrando {a}–{b} de {n}",
          noData: "No hay datos disponibles",
          selectToView: "Selecciona una sección y un periodo para ver los scores",
          totalAnalyzed: "Total de viviendas analizadas:",
          legendOk: "OK (≤33%)",
          legendWatch: "Vigilar (34-66%)",
          legendUrgent: "Inspección urgente (>66%)",
          showing: "Mostrando:",
          createIncident: "Crear incidencia",
          requestInspection: "Solicitar inspección",
          toggleIncidents: "Incidencias ({n})",
          mapSizeHint: "El tamaño del punto refleja el coste esperado; ①–⑤ marcan las mayores prioridades.",
          sortHint: "Ordenar por esta columna",
          sortedByCostNote: "Ordenada por coste esperado (probabilidad × consumo medio × tarifa), de mayor a menor. Pulsa una columna para reordenar.",
          anomaliesTitle: "Anomalías detectadas",
          noAnomalies: "Sin anomalías en las lecturas",
          histTitle: "Histórico de lecturas",
          seccionAll: "Ver todas las secciones",
          loadingSections: "Cargando sección {done} de {total}...",
          colSeccion: "Sección",
          legendSections: "Secciones",
          sectionsWithData: "{n} de {m} secciones con datos en este período",
          breakdownTitle: "Desglose por sección",
          breakdownColSeccion: "Sección",
          breakdownColViviendas: "Viviendas",
          breakdownColUrgentes: "Urgentes",
          breakdownColImpacto: "Impacto",
          breakdownColCoste: "Coste (est.)",
          breakdownHint: "Pulsa una sección para verla en detalle.",
        },
        mapa: {
          title: "Predicciones de fuga",
          subtitle: "Análisis detallado de probabilidades de fuga por vivienda",
          seccionLabel: "Sección",
          periodoLabel: "Período",
          selectSeccion: "Selecciona una sección",
          selectPeriodo: "Selecciona un período",
          probabilitiesSummary: "Resumen de probabilidades",
          low: "Baja",
          medium: "Media",
          high: "Alta",
          totalAnalyzed: "Total de viviendas analizadas:",
          mapTitle: "Mapa de probabilidades de fuga",
          searchHousing: "Buscar vivienda...",
          colHousing: "Vivienda",
          colPeriod: "Periodo",
          colProb: "Prob.",
          noData: "No hay datos disponibles",
          selectToView: "Selecciona una sección y un periodo para ver los scores",
          probabilityTitle: "Probabilidad de fuga",
          tooltipPeriodo: "Período:",
          tooltipLeakProb: "Probabilidad de fuga:",
          tooltipRisk: "Nivel de riesgo:",
          legendLow: "Baja probabilidad (≤33%)",
          legendMedium: "Media probabilidad (34-66%)",
          legendHigh: "Alta probabilidad (>66%)",
          showing: "Mostrando:",
        },
        mapa2: {
          title: "Consumo excesivo",
          subtitle: "Análisis de viviendas con consumo anómalamente elevado",
          seccionLabel: "Sección",
          periodoLabel: "Período",
          selectSeccion: "Selecciona una sección",
          selectPeriodo: "Selecciona un período",
          searchHousing: "Buscar vivienda...",
          colHousing: "Vivienda",
          colPeriod: "Periodo",
          colProb: "Prob.",
          noData: "No hay datos disponibles",
          selectToView: "Selecciona una sección y un periodo para ver los scores",
          probabilitiesSummary: "Resumen de consumo excesivo",
          mapTitle: "Mapa de consumo excesivo",
          excessTitle: "Probabilidad de consumo excesivo",
          tooltipPeriodo: "Período:",
          tooltipExcessProb: "Probabilidad de consumo excesivo:",
          tooltipRisk: "Nivel de riesgo:",
          low: "Baja",
          medium: "Media",
          high: "Alta",
          totalAnalyzed: "Total de viviendas analizadas:",
          legendLow: "Baja probabilidad (≤33%)",
          legendMedium: "Media probabilidad (34-66%)",
          legendHigh: "Alta probabilidad (>66%)",
          showing: "Mostrando:",
        },
        solicitudes: {
          title: "Solicitudes",
          subtitle: "Monitoriza y gestiona tus solicitudes de análisis",
          completed: "Completadas:",
          inProgress: "En proceso:",
          pending: "Pendientes:",
          activeFilter: "Filtro activo:",
          filterNone: "Ninguno",
          statusCompleted: "Completado",
          statusInProgress: "En Proceso",
          statusPending: "Pendiente",
        },
        incidencias: {
          title: "Incidencias",
          subtitle: "Incidencias reportadas por los ciudadanos del municipio",
          total: "Total:",
          pending: "Pendientes:",
          completed: "Completadas:",
          noIncidents: "No hay incidencias reportadas",
          pendingTitle: "Incidencias pendientes",
          completedTitle: "Incidencias completadas",
          viewImages: "Ver imágenes",
          markCompleted: "Marcar como completada",
          markPending: "Marcar como pendiente",
        },
        lecturas: {
          title: "Lecturas",
          subtitle: "Registro de lecturas de consumo de todas las viviendas",
          searchPlaceholder: "Buscar por vía, número, bloque, planta, puerta, sección, fecha...",
          addBtn: "Añadir lectura",
          modalTitle: "Nueva lectura",
          housingLabel: "Vivienda",
          housingPH: "Selecciona una vivienda",
          dateLabel: "Fecha de registro",
          counterLabel: "Lectura del contador (m³)",
          cancel: "Cancelar",
          saving: "Guardando...",
          save: "Guardar lectura",
          deleteTitle: "¿Eliminar lectura?",
          deleteDesc: "Esta acción no se puede deshacer.",
          deleteCancelBtn: "Cancelar",
          deleteConfirmBtn: "Eliminar",
          loadingText: "Cargando lecturas...",
          noResults: "No se encontraron resultados",
          noReadings: "No hay lecturas registradas",
          colVia: "Vía",
          colNum: "Nº",
          colBloque: "Bloque",
          colPlanta: "Planta",
          colPuerta: "Puerta",
          colSeccion: "Sección",
          colLectura: "Lectura (m³)",
          colFecha: "Fecha",
          tabAnomalies: "Anomalías",
          tabAll: "Todas",
          anomalyZero: "Lectura en cero",
          anomalySpike: "Salto brusco",
          anomalyDrop: "Caída brusca",
          noAnomalies: "No se han detectado anomalías en este conjunto de lecturas",
        },
        administracion: {
          title: "Administración",
          subtitle: "Gestiona los ciudadanos y usuarios del sistema",
          searchCitizenPlaceholder: "Buscar ciudadano...",
          searchHousingPlaceholder: "Buscar vivienda...",
          colEmail: "Email",
          colName: "Nombre",
          colLastName: "Apellidos",
          colPhone: "Teléfono",
          namePlaceholder: "Ej: Juan",
          lastNamePlaceholder: "Ej: García López",
          emailPlaceholder: "Ej: usuario@ejemplo.com",
          dniPlaceholder: "Ej: 12345678A",
          phonePlaceholder: "Ej: 600123456",
          registerTitle: "Registrar nuevo ciudadano",
          registerDesc: "Crea una cuenta para un ciudadano de tu municipio",
          nombreLabel: "Nombre *",
          apellidosLabel: "Apellidos *",
          emailLabel: "Email *",
          dniLabel: "DNI *",
          telefonoLabel: "Teléfono (opcional)",
          registering: "Registrando...",
          registerBtn: "Registrar ciudadano",
          privacyNotice: "Los datos introducidos serán tratados conforme a nuestra ",
          helpText: "Se generará una contraseña automática y se enviará un correo al usuario con instrucciones para cambiarla.",
          pendingUsersTitle: "Usuarios pendientes de validación",
          pendingUsersDesc: "Ciudadanos que han solicitado acceso y están esperando tu aprobación",
          noPendingUsers: "No hay usuarios pendientes de validación",
          allUsersTitle: "Todos los ciudadanos",
          allUsersDesc: "Lista completa de ciudadanos validados en tu municipio",
          noUsers: "No hay ciudadanos registrados",
        },
        configuracion: {
          title: "Configuración",
          subtitle: "Administra tu cuenta y preferencias",
          changePassword: "Cambiar contraseña",
          newPasswordLabel: "Nueva contraseña",
          confirmPasswordLabel: "Confirmar nueva contraseña",
          minPasswordPlaceholder: "Mínimo 6 caracteres",
          repeatPasswordPlaceholder: "Repite la contraseña",
          updating: "Actualizando...",
          updatePassword: "Actualizar Contraseña",
          notificationsTitle: "Notificaciones",
          emailNotifications: "Notificaciones por email",
          emailNotificationsDesc: "Recibe actualizaciones por correo electrónico",
          saveButton: "Guardar configuración",
          saving: "Guardando...",
          saveNotificationsBtn: "Guardar preferencias",
          accountTitle: "Información de la cuenta",
          orgLabel: "Organización",
          emailLabel: "Email",
          roleLabel: "Rol",
          roleInstitution: "Institución",
          roleCitizen: "Ciudadano",
          roleUnknown: "Desconocido",
          tarifaTitle: "Tarifa del agua",
          tarifaDesc: "Precio por m³ usado para estimar el impacto económico de fugas y consumo excesivo",
          tarifaLabel: "Precio por m³ (€)",
          tarifaDecimalHint: "(el decimal se escribe con coma, ej. 1,80)",
          tarifaSaveBtn: "Guardar tarifa",
          tarifaSaved: "Tarifa guardada",
        },
        cargar: {
          title: "Cargar datos",
          subtitle: "Carga archivos de datos para su análisis",
        },
        resumen: {
          title: "Resumen",
          subtitle: "Visualiza las viviendas con mayor y menor puntuación",
          loading: "Cargando scores...",
          excessTitle: "Mayor probabilidad de consumo excesivo",
          leakTitle: "Mayor probabilidad de fuga",
          period: "Período:",
          noData: "No hay datos disponibles",
          seccionLabel: "Sección",
          periodoLabel: "Período",
          pendingRequestsTitle: "Solicitudes pendientes",
          pendingRequestsDesc: "Seguimiento de procesos en curso",
          kpiWaterAtRisk: "Agua en riesgo",
          kpiHighRiskHomes: "Viviendas en riesgo alto",
          kpiOpenIncidents: "Incidencias abiertas",
          kpiConsumptionChange: "Consumo vs periodo anterior",
          priorityTitle: "Prioridades de hoy",
          prioritySubtitle: "Viviendas que más impacto tienen ahora mismo",
          priorityEmpty: "No hay viviendas prioritarias en este periodo",
          mapTitle: "Mapa de riesgo",
          kpiWaterAtRiskHint: "Suma del impacto estimado de las {n} viviendas analizadas (fugas + consumo excesivo).",
        },
        informes: {
          title: "Informes",
          subtitle: "Genera informes imprimibles a partir de los datos del panel",
          seccionLabel: "Sección",
          periodoLabel: "Período",
          selectSeccion: "Selecciona una sección",
          selectPeriodo: "Selecciona un período",
          templateLabel: "Plantilla",
          templateTecnico: "Informe técnico de riesgos",
          templateTecnicoDesc: "KPIs, puntos de actuación prioritarios, mapa y anexo de anomalías.",
          templateEficiencia: "Informe de eficiencia hídrica",
          templateEficienciaDesc: "Orientado a justificación ante Augas de Galicia y subvenciones de eficiencia hídrica.",
          generateBtn: "Generar informe",
          selectToGenerate: "Selecciona una sección y un período para generar el informe",
          generatingBtn: "Generando…",
          generatesInBackground: "El informe se genera en segundo plano y aparece en la tabla al terminar.",
          generatingHint: "Puedes seguir usando el panel; el informe aparecerá en la tabla al terminar.",
          generatingToast: "Generando el informe…",
          generatedOk: "Informe generado y guardado.",
          generateError: "No se pudo generar el informe.",
          savedTitle: "Informes generados",
          savedSubtitle: "Cada informe se guarda al generarse. Ábrelo para editar los textos o descargar el PDF.",
          colTitulo: "Informe",
          colAmbito: "Ámbito",
          colPeriodo: "Período",
          colPlantilla: "Plantilla",
          colCreado: "Generado",
          colAcciones: "Acciones",
          ambitoConcello: "Todo el concello",
          openBtn: "Abrir",
          deleteBtn: "Borrar",
          confirmDelete: "¿Seguro que quieres borrar este informe? No se puede deshacer.",
          deletedOk: "Informe borrado.",
          deleteError: "No se pudo borrar el informe.",
          listError: "No se pudieron cargar los informes guardados.",
          emptyList: "Todavía no has generado ningún informe.",
          loadingList: "Cargando informes…",
          refresh: "Actualizar",
          prev: "Anterior",
          next: "Siguiente",
          pageOf: "de",
        },
      },
      informe: {
        downloadBtn: "Descargar PDF",
        generatingBtn: "Generando…",
        back: "Volver al panel",
        loading: "Cargando datos del informe...",
        error: "No se pudieron cargar los datos del informe.",
        generatedOn: "Generado el",
        municipality: "Municipio",
        section: "Sección",
        period: "Período",
        estimateDisclaimer: "Las cifras de m³ y € son estimaciones (probabilidad × consumo medio × tarifa) y no sustituyen a una inspección en campo.",
        editableHint: "(haz clic para editar)",
        techTitle: "Informe técnico de riesgos de agua",
        summaryHeading: "Resumen ejecutivo",
        summaryPlaceholder: "Escribe aquí un resumen ejecutivo del período: hallazgos principales, viviendas críticas y recomendación de actuación.",
        seccionesHeading: "Secciones por prioridad",
        allSecciones: "Todas las secciones",
        generateAI: "Generar con IA",
        generatingAI: "Generando…",
        aiError: "No se pudo generar el resumen. Inténtalo de nuevo.",
        aiSessionError: "Tu sesión ha caducado. Vuelve a iniciar sesión para generar el resumen.",
        aiDisclaimer: "Texto generado automáticamente a partir de los datos del período. Revísalo y edítalo antes de publicar el informe.",
        kpiHeading: "Indicadores del período",
        kpiWater: "Agua en riesgo (estimada)",
        kpiHighRisk: "Viviendas en riesgo alto",
        kpiIncidents: "Incidencias abiertas",
        kpiTotal: "Viviendas analizadas",
        topHeading: "Puntos de actuación prioritarios (top 10)",
        colHousing: "Vivienda",
        colProb: "Prob.",
        colImpact: "Impacto",
        colCost: "Coste (est.)",
        colStatus: "Estado",
        mapHeading: "Mapa de riesgo",
        anomaliesHeading: "Anexo: anomalías detectadas en las lecturas",
        anomalyColHousing: "Vivienda",
        anomalyColType: "Anomalía",
        anomalyColDate: "Fecha",
        noAnomalies: "No se han detectado anomalías en el período.",
        observationsHeading: "Observaciones del técnico",
        observationsPlaceholder: "Anota observaciones de campo, decisiones tomadas y próximos pasos.",
        effTitle: "Informe de eficiencia hídrica",
        effSubtitle: "Documento de apoyo para justificación ante Augas de Galicia y solicitudes de subvención de eficiencia hídrica",
        lossesHeading: "Volumen estimado de pérdidas y coste asociado",
        lossesDesc: "Estimación agregada del agua en riesgo por fugas y consumo excesivo en el período analizado.",
        statusBreakdownHeading: "Distribución de viviendas por estado",
        prioritiesHeading: "Relación de puntos de actuación prioritarios",
        manualDataHeading: "Datos de red (cumplimentación manual)",
        injectedLabel: "Volumen de agua inyectada en la red (m³)",
        anrLabel: "Agua no registrada / no facturada (m³)",
        iliLabel: "Índice de pérdidas (ILI u otro indicador)",
        manualHint: "Estos datos no se calculan automáticamente todavía; complétalos manualmente o consulta el TODO del proyecto.",
        actionsHeading: "Actuaciones realizadas",
        actionsPlaceholder: "Describe las actuaciones ejecutadas: inspecciones, reparaciones, sustitución de contadores, campañas de sensibilización...",
        measuresHeading: "Medidas previstas",
        measuresPlaceholder: "Describe las medidas previstas y el calendario estimado.",
        saveBtn: "Guardar cambios",
        retrySave: "Reintentar guardado",
        saving: "Guardando…",
        saved: "Guardado",
        unsaved: "Cambios sin guardar",
        savedOk: "Informe guardado.",
        saveError: "No se pudo guardar el informe.",
      },
      upload: {
        title: "Cargar archivo de datos",
        subtitle: "Sube un archivo con los datos de la solicitud",
        fileLabel: "Archivo",
        formats: "📁 Formatos: CSV, XLSX.",
        uploadBtn: "Subir",
        selectFile: "Seleccionar archivo",
        noFileSelected: "Ningún archivo seleccionado",
        privacyNotice: "Al subir este archivo confirmas que su tratamiento cumple con nuestra ",
        privacyLink: "Política de Privacidad",
      },
      imageModal: {
        title: "Imágenes de incidencia",
      },
      messages: {
        incidentCompleteError: "Error al marcar la incidencia como completada. Por favor, inténtalo de nuevo.",
        incidentPendingError: "Error al marcar la incidencia como pendiente. Por favor, inténtalo de nuevo.",
        userRegistered: "Usuario registrado correctamente. Se ha enviado un email con las instrucciones.",
        userDeleted: "Usuario eliminado correctamente",
        userDeleteError: "Error al eliminar usuario",
        confirmDeleteUserPrefix: "¿Estás seguro de que deseas eliminar permanentemente al usuario",
        confirmDeleteUserSuffix: "?\n\nEsta acción no se puede deshacer y eliminará todos sus datos.",
        confirmIncidentPending: "¿Estás seguro de que quieres marcar esta incidencia como pendiente?",
        addressUnavailable: "Dirección no disponible",
      },
    },
    ciudadano: {
      pageTitle: "Panel personal | EGS",
      privacyFooter: "Los datos que introduces en esta aplicación son tratados conforme a nuestra ",
      session: {
        loading: "Cargando",
        notStarted: "Sesión no iniciada",
        notStartedDesc: "No has iniciado sesión o tu sesión ha expirado.",
        backToLogin: "Volver a Iniciar Sesión",
      },
      menu: {
        inicio: "Inicio",
        addVivienda: "Añadir vivienda",
        consultarViviendas: "Consultar viviendas",
        addLectura: "Añadir lectura",
        consultarLecturas: "Consultar lecturas",
        addIncidencia: "Añadir incidencia",
        consultarIncidencias: "Consultar incidencias",
        configuracion: "Configuración",
      },
      sidebar: {
        logout: "Cerrar sesión",
        logoutDialogTitle: "Cerrar sesión",
        logoutDialogDesc: "¿Estás seguro de que quieres cerrar sesión?",
        logoutCancel: "Cancelar",
        logoutConfirm: "Cerrar sesión",
      },
      validation: {
        fillAllFields: "❌ Debes completar todos los campos",
        passwordNoMatch: "❌ Las contraseñas no coinciden",
        passwordTooShort: "❌ La contraseña debe tener al menos 6 caracteres",
        passwordUpdated: "✅ Contraseña actualizada. Redirigiendo al login...",
        serverError: "El servidor no devolvió una respuesta válida",
      },
      messages: {
        incidentDeleteError: "Error al eliminar la incidencia. Por favor, inténtalo de nuevo.",
        notifLoadError: "No se pudieron cargar las preferencias de notificación",
        notifSaveError: "No se pudieron guardar las preferencias",
        unknownMunicipality: "Concello desconocido",
      },
      sections: {
        inicio: {
          title: "Inicio",
          welcome: "Bienvenido, ",
          addHousingTitle: "Añadir vivienda",
          addHousingDesc: "Registra una nueva vivienda",
          viewHousingTitle: "Consultar viviendas",
          addReadingTitle: "Añadir lectura",
          addReadingDesc: "Registra lecturas del contador",
        },
        addVivienda: {
          title: "Añadir vivienda",
          subtitle: "Registra una nueva vivienda en tu cuenta",
          formTitle: "Nueva vivienda",
          viaLabel: "Vía",
          viaPH: "Seleccione una vía",
          numeroLabel: "Número",
          numeroPH: "Seleccione un número",
          noNumero: "Sin número",
          bloqueLabel: "Bloque",
          bloquePH: "Seleccione un bloque",
          noBloque: "Sin bloque",
          plantaLabel: "Planta",
          plantaPH: "Seleccione una planta",
          noPlanta: "Sin planta",
          puertaLabel: "Puerta",
          puertaPH: "Seleccione una puerta",
          noPuerta: "Sin puerta",
          cancel: "Cancelar",
          save: "Guardar vivienda",
          cusecInfoBefore: "ℹ️ El código CUSEC se asignará automáticamente como",
          cusecDesc: "(Concello de Cerdedo)",
        },
        consultarViviendas: {
          title: "Consultar viviendas",
          subtitle: "Viviendas registradas",
          tableTitle: "Viviendas registradas",
          searchPlaceholder: "Buscar vivienda...",
          noHousing: "No hay viviendas registradas",
          addFirstHousing: "Añadir primera vivienda",
          noResults: "No se encontraron viviendas que coincidan con la búsqueda",
          colVivienda: "Vivienda",
          colPeriod: "Período",
          colType: "Tipo",
          colProb: "Prob.",
          colRiesgo: "Riesgo",
          colAcciones: "Acciones",
          leakProb: "Probabilidad de fuga",
          excessConsumption: "Consumo excesivo",
          noData: "Sin datos",
          pending: "⏳ Pendiente",
          deleteHousing: "Eliminar vivienda",
          period: "Período:",
          scoreLabel: "Score:",
          riskLabel: "Riesgo:",
          riskLow: "Bajo",
          riskMedium: "Medio",
          riskHigh: "Alto",
        },
        addLectura: {
          title: "Añadir lectura",
          subtitle: "Registra una nueva lectura del contador",
          formTitle: "Nueva lectura",
          noHousing: "No hay viviendas registradas",
          addHousingFirst: "Añadir vivienda primero",
          housingLabel: "Vivienda",
          housingPH: "Selecciona una vivienda",
          dateLabel: "Fecha de registro",
          counterLabel: "Lectura del contador",
          cancel: "Cancelar",
          save: "Guardar lectura",
        },
        consultarLecturas: {
          title: "Consultar lecturas",
          subtitle: "Lecturas registradas",
          tableTitle: "Lecturas registradas",
          searchPlaceholder: "Buscar por vivienda...",
          noReadings: "No hay lecturas registradas",
          addReading: "Añadir lectura",
          noResults: "No se encontraron lecturas que coincidan con la búsqueda",
          colVivienda: "Vivienda",
          colFecha: "Fecha",
          colLectura: "Lectura",
          viviendaLabel: "Vivienda:",
          fechaLabel: "Fecha:",
          lecturaLabel: "Lectura:",
        },
        addIncidencia: {
          title: "Añadir incidencia",
          subtitle: "Reporta una nueva incidencia",
          formTitle: "Nueva incidencia",
          noHousing: "No hay viviendas registradas",
          addHousingFirst: "Añadir vivienda primero",
          housingLabel: "Vivienda",
          housingPH: "Selecciona una vivienda",
          subjectLabel: "Asunto",
          subjectPH: "Escribe un resumen breve",
          descLabel: "Descripción",
          descPH: "Aquí puedes extenderte más",
          imagesLabel: "Imágenes (opcional, máximo 2)",
          addImage: "Tomar foto o seleccionar imagen",
          addAnotherImage: "Añadir otra imagen",
          cancel: "Cancelar",
          save: "Guardar incidencia",
        },
        consultarIncidencias: {
          title: "Consultar incidencias",
          subtitle: "Incidencias guardadas",
          total: "Total:",
          pending: "Pendientes:",
          completed: "Completadas:",
          noIncidents: "No hay incidencias guardadas",
          noIncidentsDesc: "Comienza reportando tu primera incidencia",
          addIncident: "Añadir incidencia",
          pendingTitle: "Incidencias pendientes",
          completedTitle: "Incidencias completadas",
          subjectLabel: "Asunto",
          descLabel: "Descripción",
          viewImages: "Ver imágenes",
          pendingBadge: "Pendiente",
          completedBadge: "Completada",
        },
        configuracion: {
          title: "Configuración",
          subtitle: "Administra tu cuenta y preferencias",
          changePassword: "Cambiar contraseña",
          newPasswordLabel: "Nueva contraseña",
          confirmPasswordLabel: "Confirmar nueva contraseña",
          minPasswordPlaceholder: "Mínimo 6 caracteres",
          repeatPasswordPlaceholder: "Repite la contraseña",
          updating: "Actualizando...",
          updatePassword: "Actualizar Contraseña",
          notificationsTitle: "Notificaciones por email",
          notificationsDesc: "Recibe actualizaciones por correo electrónico",
          saveNotifications: "Guardar",
          notificationsHeader: "Notificaciones",
          saving: "Guardando...",
          saveNotificationsBtn: "Guardar preferencias",
          accountTitle: "Información de la cuenta",
          orgLabel: "Organización",
          emailLabel: "Email",
          roleLabel: "Rol",
        },
      },
    },
  },

  gl: {
    // Header
    nav: {
      whatHappens: "Reto",
      ourSolution: "Produto",
      meetUs: "Coñézanos",
      join: "Contacto",
      login: "Iniciar sesión",
      startFree: "Comece gratis",
      news: "Novas",
    },

    // Hero Section
    hero: {
      badge: "Finalistas do Programa de Innovación Bankinter",
      badgeLine1: "Finalistas do Programa",
      badgeLine2: "de Innovación Bankinter",
      title: "Reduza fugas.",
      titleHighlight: "Dixitalice a súa xestión da auga.",
      subtitle: "O software de xestión para concellos que predí onde actuar fronte a fugas de auga, monitoriza consumos e conecta aos cidadáns. Adeus ao caos. Ola a EGS.",
      ctaPrimary: "Comece gratis",
      newsTicker: "Mención nos Premios AJE Pontevedra 2026",
      ctaSecondary: "O noso equipo",
    },

    // Logo Wall
    logos: {
      heading: "Orixinado en",
    },

    // Global Water Crisis Section
    crisis: {
      title: "EGS responde á crise global da auga",
      subtitle: "Existen datos alarmantes que demostran a urxencia de actuar agora:",
      stat1: "da auga da Terra é apta para o consumo humano",
      stat2: "de persoas poderían carecer de auga potable para 2050",
      stat3: "gasta unha persoa ao día. Reducindo a 50 L, romperíase o círculo de pobreza",
      trendsTitle: "Dúas tendencias agravan a crise hídrica",
      trend1Title: "Crecemento poboacional",
      trend1Text: "A poboación mundial acadará os 9.700 millóns de persoas para 2050. O aumento da poboación leva a unha maior demanda de auga potable. Segundo a ONU, 1.800 millóns de persoas vivirán en zonas con falta de auga potable.",
      trend2Title: "Cambio climático",
      trend2Text: "O aumento de temperaturas e a alteración de patróns de chuvia reducen drasticamente a dispoñibilidade de auga doce. Segundo a OMS, un 20 % da poboación mundial carece de acceso regular a auga potable e saneamento, provocando a proliferación de enfermidades.",
    },

    // Galicia Section
    galicia: {
      title: "Galicia non é unha excepción",
      stat1: "Comunidade que máis auga perde por fugas na súa rede, segundo S&P Global Ratings",
      stat2: "de litros diarios son recuperables con reparación de fugas",
      stat3Only: "só",
      stat3: "de concellos prevén as fugas a pesar do risco de seca",
      closing: "Galicia e Asturias son as comunidades con maiores fugas de auga nas súas redes. Murcia, Andalucía e Baleares enfrontan o maior estrés hídrico.",
      closingHighlight1: "Galicia e Asturias",
      closingHighlight2: "Murcia, Andalucía e Baleares",
    },

    // Solution Section
    solution: {
      badge: "Para concellos e institucións",
      title: "A nosa solución",
      subtitle: "Sistema integral para a xestión do ciclo da auga",
      description: "Ofrecemos un sistema de xestión web que parte de ficheiros de rexistros da auga para achegar datos centralizados, outorgar control total e favorecer decisións máis intelixentes ás institucións.",
      comingSoon: "Proximamente",
      comingSoonTitle: "Tecnoloxía ao servizo da auga",
      comingSoonSubtitle: "Predicións, alertas e métricas dixitalizadas para reducir as fugas e o desperdicio hídrico",
      scrollTitle: "Dispoña dun sistema integral para a",
      scrollSubtitle: "Xestión da Auga",
      systemIntro: "Controlamos e reducimos o consumo de auga non rexistrado mediante a detección de fugas e patróns de gasto. O sistema permite a",
      systemTarget: "concellos e institucións",
      systemAllows: ":",
      feature1Bullet: "Obter un <strong>mapa priorizado</strong> de <strong>puntos de actuación</strong>, mediante predicións e alertas dixitalizadas",
      feature2Bullet: "Calcular sobrecustos estimados das perdas",
      feature3Bullet: "Reportar lecturas de contadores e incidencias dos cidadáns",
      description2: "Dividimos o servizo en 3 capas principais",
      stepsTitle: "Como funciona EGS",
      feature1Title: "IA predictiva",
      feature1Text: "Algoritmos capaces de anticiparse aos problemas, predicindo e alertando antes de que ocorran para optimizar o mantemento dos sistemas hídricos.",
      feature2Title: "Análise avanzada",
      feature2Text: "Dashboards en tempo real que analizan patróns de consumo, predín a demanda de recursos e estiman custos asociados de forma detallada.",
      feature3Title: "Xestión completa",
      feature3Text: "Proposta de solucións sostibles fronte ás fugas ou vulnerabilidades detectadas, garantindo unha xestión da auga máis eficiente e responsable.",
      videoHeadline1: "Control total para o seu concello",
      videoHeadline2: "A auga, nas mans do cidadán",
      videoPill1_1: "Mapa de fugas",
      videoPill1_2: "Alertas preditivas",
      videoPill1_3: "Xestión técnica",
      videoPill2_1: "Consumo en tempo real",
      videoPill2_2: "Reporte de incidencias",
    },

    // About Us Section
    about: {
      title: "Coñézanos",
      intro: "En EGS, somos un equipo multidisciplinar de emprendedores galegos cunha misión clara: transformar a xestión da auga a nivel global, comezando dende as nosas cidades, vilas e pobos, pero coa ambición de marcar a diferenza a gran escala.",
      missionTitle: "A nosa misión",
      missionText: "Garantir acceso universal a auga limpa mediante tecnoloxía con propósito e xestión intelixente.",
      purposeTitle: "O noso propósito",
      purposeText: "Crear un movemento global que transforme a xestión da auga para as xeracións futuras.",
      keyTitle: "A nosa clave",
      keyText: "Para lograr un impacto duradeiro, combinamos colaboración con coñecemento compartido.",
      teamIntro: "Unha organización constrúese a partir das súas persoas. Detrás de EGS hai un equipo diverso e apaixonado polo que fai e o impacto que busca xerar.",
      luis: {
        name: "Luis Encinas",
        role: "Relacións Institucionais e Finanzas",
        bio: "Emprendedor con firme compromiso social e visión transformadora do impacto tecnolóxico.",
      },
      ignacio: {
        name: "Ignacio Garbayo",
        role: "Desenvolvemento e Intelixencia Artificial",
        bio: "Da vida ao que desenvolve coa convicción de que a tecnoloxía transformará a xestión da auga.",
      },
      martin: {
        name: "Martín Sende",
        role: "Solucións de Xestión e Sostibilidade",
        bio: "Apaixonado pola ciencia e convencido do impacto real da tecnoloxía sobre os recursos naturais.",
      },
      viewProfile: "Ver perfil",
    },

    // FAQ Section
    faq: {
      title: "Preguntas frecuentes",
      subtitle: "Respondemos as dúbidas máis habituais sobre EGS e como traballamos cos concellos.",
      items: [
        {
          question: "¿Necesitamos instalar sensores ou cambiar os contadores?",
          answer: "Non. EGS funciona coas lecturas de contadores que o concello xa recolle; non fai falta instalar hardware nin substituír nada para comezar. Adaptamos o funcionamento ao voso modelo.",
        },
        {
          question: "¿De quen son os datos e como se protexe a privacidade?",
          answer: "Os datos son sempre do concello; EGS só os procesa para prestar o servizo, conforme ao RXPD e aloxados na UE.",
        },
        {
          question: "¿En que consiste o piloto?",
          answer: "É gratuíto. O concello só achega un histórico de lecturas; ao rematar recibe un mapa priorizado de posibles fugas, sen compromiso.",
        },
        {
          question: "¿Como se contrata dende unha administración pública?",
          answer: "Tras o piloto, EGS pode contratarse por contrato menor, subscrición ou integrarse nun prego, con acompañamento durante todo o proceso.",
        },
        {
          question: "¿Quen está detrás de EGS?",
          answer: "Un equipo galego xurdido na USC, respaldado pola Fundación Innovación Bankinter e a Fundación Princesa de Girona.",
        },
      ],
      knowUsLinkIntro: "Máis na sección",
      knowUsLinkText: "Coñézanos",
      linkedinLinkIntro: "ou no noso",
      linkedinLinkText: "LinkedIn",
    },

    // Join Section
    join: {
      title: "Únete",
      subtitle: "Sé parte do cambio. Buscamos institucións e colaboradores que busquen transformar a xestión da auga e crear un futuro máis sostible para as próximas xeracións.",
      whyTitle: "Comece agora con EGS",
      impactTitle: "Impacto",
      impactText: "Detecte fugas antes de que se convertan en avarías. Menos custo, máis auga aproveitada.",
      commitmentTitle: "Compromiso",
      commitmentText: "Integrāmonos coa súa infraestrutura actual. Sen obras, sen hardware adicional.",
      techTitle: "Tecnoloxía",
      techText: "Dashboards en tempo real con alertas automáticas. Xestione toda a rede desde unha soa pantalla.",
      contactTitle: "Contacte directamente",
      formTitle: "Comece a súa aventura",
      formSubtitle: "Poñerémonos en contacto con vostede o antes posible.",
      formName: "Nome",
      formLastName: "Apelidos",
      formEmail: "Correo electrónico",
      formCollaboration: "Cargo na organización",
      formCollaborationPlaceholder: "Selecciona unha opción",
      formCollaborationOptions: {
        partner: "Técnico/a de Augas",
        client: "Técnico/a de Infraestruturas",
        investor: "Responsable de Servizos Municipais",
        talent: "Alcalde/Concelleiro/Funcionario",
        other: "Outro cargo",
      },
      formMessage: "Cóntenos sobre o seu concello",
      formMessagePlaceholder: "Organización ou concello, poboación aproximada (hab.), teléfono de contacto e situación actual da rede...",
      formPrivacy: "Acepto a",
      formPrivacyLink: "política de privacidade",
      formSubmit: "Solicitar piloto",
      formSubmitting: "Enviando...",
      formSuccessMessage: "Grazas! Recibimos a túa solicitude. Contactarémoste pronto.",
      formErrorPrivacy: "Debes aceptar a política de privacidade",
      formErrorRequired: "Por favor completa todos os campos obrigatorios",
      formErrorGeneral: "Houbo un erro ao enviar a túa solicitude. Por favor téntao de novo.",
    },

    // Success Modal
    successModal: {
      title: "Solicitude recibida",
      message: "Rexistramos a túa solicitude correctamente. O noso equipo poñerase en contacto contigo nun prazo de 24 a 48 horas.",
      button: "Pechar",
    },

    loadingModal: {
      message: "Por favor, agarda...",
      videoLoading: "Cargando..."
    },

    // Welcome Email
    welcomeEmail: {
      subject: "Benvido a EGS - Confirmación de rexistro",
      greeting: "Ola",
      title: "Benvido a EGS",
      message: "Grazas por rexistrarte na nosa plataforma de xestión intelixente da auga.",
      received: "Recibimos os teus datos correctamente e están sendo procesados.",
      pending: "A túa solicitude está pendente de validación por parte da túa institución.",
      nextSteps: "Que segue:",
      step1: "O noso equipo revisará a túa información",
      step2: "A túa institución validará o teu acceso",
      step3: "Recibirás un correo cando a túa conta estea activa",
      timeframe: "Este proceso adoita completarse en 24-48 horas.",
      questions: "Tes algunha dúbida?",
      contactUs: "Non dubides en contactarnos en",
      thanks: "Grazas por unirte á revolución da auga",
      footer: "EGS",
      footerTagline: "Dende Galicia, transformamos a xestión da auga.",
    },

    // Password Change Page
    passwordChange: {
      title: "Cambiar contrasinal",
      subtitle: "Configura o teu novo contrasinal de acceso",
      newPassword: "Novo contrasinal",
      confirmPassword: "Confirmar contrasinal",
      submit: "Cambiar contrasinal",
      success: "Contrasinal cambiado correctamente. Redirixindo ao inicio de sesión...",
      errorMinLength: "O contrasinal debe ter polo menos 8 caracteres",
      errorMismatch: "Os contrasinais non coinciden",
      errorInvalidToken: "A ligazón é inválida ou caducou",
      errorAlreadyUsed: "Esta ligazón xa foi utilizada",
      errorGeneral: "Erro ao cambiar o contrasinal. Téntao de novo.",
      loading: "Validando ligazón...",
    },

    // Credentials Email
    credentialsEmail: {
      subject: "A túa conta en EGS foi creada",
      title: "Conta creada",
      greeting: "Ola",
      message: "A túa conta na plataforma EGS foi creada polo teu concello.",
      instructions: "Para comezar a usar a túa conta, necesitas establecer o teu contrasinal.",
      buttonText: "Cambiar o meu contrasinal",
      note: "Esta ligazón é válida por 24 horas.",
      footer: "Se non solicitaches esta conta, contacta co teu concello.",
      privacyNotice: "Os teus datos persoais son tratados por EGS conforme ao Regulamento Xeral de Protección de Datos (RXPD). Podes consultar a nosa Política de Privacidade en: ",
    },

    // Footer
    footer: {
      description: "Dende Galicia, transformamos a xestión da auga a través de intelixencia artificial e automatización de procesos para crear un futuro máis sostible.",
      backedBy: "Respaldados por",
      contact: "Contacto",
      quickLinks: "Ligazóns rápidas",
      home: "Inicio",
      copyright: "© 2026 EGS. Todos os dereitos reservados.",
      privacy: "Política de privacidade",
      legal: "Aviso legal",
      cookies: "Política de cookies",
    },

    // Novas (posts de LinkedIn) — /{lang}/news
    news: {
      metaTitle: "Novas | EGS",
      metaDescription: "As últimas publicacións de EGS en LinkedIn: auga, concellos e tecnoloxía en Galicia.",
      eyebrow: "Dende a nosa páxina de LinkedIn",
      title: "Novas",
      subtitle: "O que contamos sobre auga, concellos e tecnoloxía en Galicia. Cada publicación enlaza ao post orixinal.",
      originalLanguage: "As publicacións móstranse no idioma no que se escribiron en LinkedIn.",
      follow: "Seguir a EGS en LinkedIn",
      featured: "Destacado",
      readPost: "Ler publicación",
      allPosts: "Todas as publicacións",
      sortedNote: "Da máis recente á máis antiga",
      readMore: "Ler máis",
      showMore: "Ver máis publicacións",
      empty: "Aínda non hai publicacións. Mentres tanto, pode seguirnos en LinkedIn.",
      ctaTitle: "Non perda o seguinte",
      ctaText: "Sígannos en LinkedIn para ver cada novidade o día que a publicamos.",
      ctaButton: "Seguir en LinkedIn",
      back: "Volver ás novas",
      publishedOn: "Publicado en LinkedIn",
      reactions: "reaccións",
      comments: "comentarios",
      viewOnLinkedIn: "Ver en LinkedIn",
      copyLink: "Copiar ligazón",
      linkCopied: "Ligazón copiada",
      morePosts: "Máis publicacións",
      seeAll: "Ver todas",
      image: "Imaxe",
      shared: "Compartido",
      sharedBy: "Compartido por EGS",
      sharedFrom: "Publicación orixinal de",
      linkedinPost: "Publicación de LinkedIn",
    },

    // Login Page
    loginPage: {
      title: "Inicia sesión",
      subtitle: "Accede á túa conta de EGS",
      welcomeBack: "Benvido de volta",
      welcomeDescription: "Entra para acceder ao teu panel de control",
      email: "Correo electrónico",
      emailPlaceholder: "teu@email.com",
      password: "Contrasinal",
      passwordPlaceholder: "••••••••",
      loginButton: "Iniciar sesión",
      loggingIn: "Iniciando sesión...",
      errorCredentials: "Credenciais incorrectas",
      errorGeneral: "Erro ao iniciar sesión",
      errorAccountNotValidated: "A túa conta aínda non foi validada polo administrador. Por favor, agarda a que se aprobe o teu rexistro.",
      noAccount: "Non tes unha conta?",
      signUpLink: "Rexístrate",
      orDivider: "ou",
      googleLogin: "Continuar con Google",
      googleLoggingIn: "Conectando con Google...",
      errorGoogle: "Erro ao iniciar sesión con Google",
    },

    // Sign Up Page
    signupPage: {
      title: "Rexístrate",
      subtitle: "Crea a túa conta de EGS",
      welcome: "Únete a EGS",
      welcomeDescription: "Completa o formulario para solicitar acceso",
      dni: "DNI",
      dniPlaceholder: "12345678A",
      nombre: "Nome",
      nombrePlaceholder: "O teu nome",
      apellidos: "Apelidos",
      apellidosPlaceholder: "Os teus apelidos",
      email: "Correo electrónico",
      emailPlaceholder: "teu@email.com",
      password: "Contrasinal",
      passwordPlaceholder: "••••••••",
      confirmPassword: "Repetir contrasinal",
      confirmPasswordPlaceholder: "••••••••",
      telefono: "Teléfono (opcional)",
      telefonoPlaceholder: "612345678",
      concello: "Concello",
      concelloPlaceholder: "Selecciona o teu concello",
      notifications: "Recibir notificacións por correo",
      notificationsHelper: "Só para alertas sobre as túas vivendas. Os correos informativos da túa conta enviaranse automaticamente.",
      signupButton: "Rexistrarse",
      signingUp: "Rexistrando...",
      haveAccount: "Xa tes conta?",
      loginLink: "Inicia sesión",
      errorDniFormat: "Formato de DNI inválido (8 díxitos + letra)",
      errorDniRequired: "O DNI é obrigatorio",
      errorNombreRequired: "O nome é obrigatorio",
      errorApellidosRequired: "Os apelidos son obrigatorios",
      errorEmailFormat: "Formato de correo electrónico inválido",
      errorEmailRequired: "O correo electrónico é obrigatorio",
      errorPasswordRequired: "O contrasinal é obrigatorio",
      errorPasswordLength: "O contrasinal debe ter polo menos 8 caracteres",
      errorPasswordMismatch: "Os contrasinais non coinciden",
      errorConcelloRequired: "Debes seleccionar un concello",
      errorGeneral: "Erro ao rexistrarse. Por favor, téntao de novo.",
      errorLoadingConcellos: "Erro ao cargar os concellos",
      googleSignup: "Rexistrarse con Google",
      googleSignupDescription: "O teu nome e email encheranse automaticamente dende a túa conta de Google. Deberás completar o resto de datos.",
      orDivider: "ou",
      privacyCheckbox: "Lin e acepto a ",
      privacyLink: "Política de Privacidade",
      errorPrivacyRequired: "Debes aceptar a política de privacidade para rexistrarte",
    },

    // Sign Up Success Page
    signupSuccess: {
      title: "Solicitude enviada",
      subtitle: "O teu rexistro está pendente de aprobación",
      message: "A túa solicitude foi enviada á túa institución",
      description: "Recibirás unha notificación por correo electrónico cando o teu perfil sexa aprobado e poidas acceder á plataforma.",
      emailNote: "Nota importante",
      emailNoteText: "O permiso de notificacións que outorgaches durante o rexistro refírese unicamente a alertas sobre as túas vivendas. Os correos informativos relacionados coa túa conta enviaranse automaticamente ao darte de alta.",
      spamNote: "Non ves o correo?",
      spamNoteText: "Se non atopas o correo de benvida na túa bandexa de entrada, revisa a túa carpeta de spam ou promocións. Por favor, marca os nosos correos como 'Non é spam' para recibir futuras notificacións.",
      backToHome: "Volver ao inicio",
      goToLogin: "Ir a iniciar sesión",
    },

    // Legal Pages
    legal: {
      privacy: {
        title: "Política de privacidade",
        subtitle: "Información sobre como EGS recompila, usa e protexe os teus datos persoais",
        intro: "En <strong>EGS</strong> valoramos a privacidade dos nosos usuarios, propietarios, concellos e de todas as persoas cuxos datos tratamos. Cumprimos co Regulamento (UE) 2016/679 (RXPD) e coa Lei Orgánica 3/2018, de Protección de Datos Persoais e garantía dos dereitos dixitais (LOPDGDD).",
        section1Title: "1. Responsable do tratamento",
        section1Content: ["Denominación: EGS (constitución societaria en proceso)", "Domicilio: Galicia, España (enderezo definitivo pendente de rexistro)", "Contacto: info@egswater.com"],
        section2Title: "2. Datos que tratamos",
        section2Content: "Tratamos datos como nome, apelidos, enderezo, datos de identificación e contacto, datos de vivenda, catastrais, consumos de auga, datos do censo e de propietarios, entre outros. Parte destes datos son achegados directamente polos concellos cos que EGS ten relación contractual; neses casos EGS actúa como encargado do tratamento conforme ao Art. 28 RXPD.",
        section3Title: "3. Finalidades e base legal",
        section3Content: [
          { purpose: "Xestión de relacións con concellos e propietarios", basis: "Art. 6.1.b RXPD — execución de contrato" },
          { purpose: "Estudos, auditorías e servizos sobre consumo de auga", basis: "Art. 6.1.b / Art. 6.1.e RXPD — execución de contrato / interese público" },
          { purpose: "Mantemento do censo de vivendas", basis: "Art. 6.1.c / Art. 6.1.e RXPD — obriga legal / interese público" },
          { purpose: "Xestión de facturación e cobros", basis: "Art. 6.1.b RXPD — execución de contrato" },
          { purpose: "Cumprimento de obrigas legais e administrativas", basis: "Art. 6.1.c RXPD — obriga legal" },
          { purpose: "Notificacións e comunicacións ao usuario (co seu consentimento)", basis: "Art. 6.1.a RXPD — consentimento do interesado" },
        ],
        section4Title: "4. Conservación dos datos",
        section4Content: "Os datos conservaranse durante os seguintes prazos: datos de conta de usuario, 3 anos desde a baixa; datos de consumo e auditoría, 5 anos desde a finalización do contrato co concello; datos de facturación, 6 anos conforme ao artigo 30 do Código de Comercio. En caso de litixio ou reclamación, os datos conservaranse ata a resolución firme.",
        section5Title: "5. Destinatarios",
        section5Content: "Os datos poderán ser comunicados a concellos, entidades colaboradoras, provedores de servizos (encargados do tratamento) e autoridades competentes, sempre garantindo o cumprimento da normativa.",
        section6Title: "6. Transferencias internacionais",
        section6Content: "EGS traballa con provedores de servizos tecnolóxicos (infraestrutura cloud, envío de email) que poden operar parcialmente fóra do Espazo Económico Europeo. Neses casos, EGS esixe a ditos provedores a subscrición de Cláusulas Contractuais Tipo (CCT) aprobadas pola Comisión Europea, ou outras garantías adecuadas conforme ao Capítulo V do RXPD.",
        section7Title: "7. Dereitos do interesado",
        section7Intro: "O usuario pode exercer os seguintes dereitos:",
        section7Content: [
          "Acceder aos seus datos",
          "Rectificalos ou suprimilos",
          "Limitar o seu tratamento",
          "Opoñerse ao tratamento",
          "Solicitar a portabilidade",
          "Retirar o seu consentimento en calquera momento",
          "Presentar reclamación ante a Axencia Española de Protección de Datos (AEPD)"
        ],
        section7LinkText: "Axencia Española de Protección de Datos (AEPD)",
        section7Exercise: "Para exercer calquera destes dereitos, envía un correo a info@egswater.com indicando o dereito que desexas exercer e adxuntando copia do teu documento de identidade. EGS responderá no prazo máximo de 1 mes, prorrogable outros 2 meses en casos de especial complexidade (Art. 12.3 RXPD).",
        section8Title: "8. Tratamento automatizado e intelixencia artificial",
        section8Content: "EGS utiliza algoritmos de análise preditiva para a detección de fugas e optimización do consumo de auga. Estas análises aplícanse sobre datos agregados de consumo e non producen decisións individuais con efectos xurídicos significativos sobre as persoas. Non se toman decisións automatizadas nos termos do Art. 22 RXPD.",
        section9Title: "9. Delegado de Protección de Datos",
        section9Content: "EGS avaliou a obrigatoriedade de designar un Delegado de Protección de Datos (DPD) conforme ao Art. 37 RXPD e conclúe que, na súa fase operativa actual, non concorren os supostos que o fan preceptivo. Esta avaliación será revisada periodicamente conforme medre o volume e tipoloxía de datos tratados.",
        section10Title: "10. Menores de idade",
        section10Content: "O servizo de EGS está dirixido exclusivamente a persoas maiores de 14 anos, conforme ao Art. 7 da LOPDGDD. EGS non recompila conscientemente datos de menores de dita idade. Se se detecta que un usuario rexistrado é menor de 14 anos, os seus datos serán eliminados de forma inmediata.",
        section11Title: "11. Medidas de seguridade",
        section11Content: "Adoptamos medidas técnicas e organizativas para protexer os datos fronte a accesos non autorizados, perdas, alteracións ou divulgacións indebidas, incluíndo control de accesos, autenticación segura, cifrado e políticas internas de seguridade.",
        section12Title: "12. Brechas de seguridade",
        section12Content: "No caso dunha brecha de seguridade que afecte a datos persoais, notificaremos á AEPD e, se procede, tamén aos afectados, o antes posible.",
        section13Title: "13. Política de cookies",
        section13Content: "O uso de cookies e tecnoloxías de rastrexo neste sitio web detállase na nosa Política de Cookies, dispoñible no pé de páxina.",
        section14Title: "14. Modificacións",
        section14Content: "Esta política pode actualizarse para adaptarse a cambios legais ou internos. No caso de cambios significativos, informarase a través da web ou outros medios adecuados.",
        lastUpdated: "Última actualización: marzo de 2026",
      },
      cookies: {
        title: "Política de cookies",
        subtitle: "Información sobre o uso de cookies neste sitio web",
        intro: "Este sitio web utiliza <strong>cookies</strong> para mellorar a experiencia do usuario, analizar o tráfico e personalizar o contido de acordo ás túas preferencias. A continuación, explicámosche de forma detallada que cookies utilizamos, con que finalidade e como podes xestionalas.",
        section1Title: "Que son as cookies?",
        section1Content: [
          "As cookies son pequenos ficheiros de texto que os sitios web almacenan no teu navegador cando os visitas. Serven para recordar as túas preferencias, facilitar a navegación e recompilar información estatística sobre o uso do sitio."
        ],
        section2Title: "Tipos de cookies que utilizamos",
        section2Content: "En función da súa finalidade, clasificamos as cookies deste sitio nas seguintes categorías:",
        section3Title: "Cookies estritamente necesarias",
        section3Content: [
          "Estas cookies son imprescindibles para o funcionamento básico do sitio web e non se poden desactivar. Utilízanse para xestionar a autenticación do usuario, manter a sesión activa e recordar as preferencias de idioma.",
          "<strong>access_token / refresh_token:</strong> Xestionan a sesión do usuario autenticado. Xéranse ao iniciar sesión e son necesarias para acceder ás áreas protexidas (panel de control). Duración: sesión (elimínanse ao pechar sesión ou expirar o token).",
          "<strong>lang</strong> (cookie, 12 meses): Recorda o idioma seleccionado polo usuario para ofrecer unha experiencia coherente entre visitas.",
          "<strong>cookieConsent</strong> (cookie, 12 meses): Garda a túa elección sobre a aceptación ou rexeitamento de cookies. Expira automaticamente tras 12 meses, momento no que se che volverá a solicitar a túa preferencia."
        ],
        section4Title: "Cookies analíticas e de rendemento",
        section4Content: [
          "Estas cookies permítennos analizar como os usuarios interactúan co sitio web, axudándonos a mellorar o seu funcionamento e contido.",
          "<strong>Vercel Analytics:</strong> Utilizamos o servizo de analítica de Vercel, que recompila datos de rendemento de forma anónima e agregada (tempos de carga, rutas visitadas). Non utiliza cookies persistentes nin identifica a usuarios individuais."
        ],
        section5Title: "Cookies de terceiros",
        section5Content: [
          "Algúns servizos integrados no noso sitio poden establecer as súas propias cookies:",
          "<strong>Google reCAPTCHA v3:</strong> Utilizado nos formularios de rexistro para verificar que o usuario é humano. Establece a cookie <strong>_GRECAPTCHA</strong> baixo o dominio <em>.google.com</em> (duración: sesión, ata 6 meses). Estas cookies son xestionadas por Google e están suxeitas á súa <a href='https://policies.google.com/privacy' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>política de privacidade</a>.",
          "<strong>Supabase:</strong> O noso provedor de base de datos e autenticación pode xerar identificadores técnicos necesarios para a comunicación co servidor."
        ],
        section6Title: "Xestión de cookies",
        section6Content: "Podes configurar o teu navegador para <span>aceptar</span>, <span>bloquear</span> ou <span>eliminar</span> cookies en calquera momento. Así mesmo, podes retirar o teu consentimento borrando a cookie <strong>cookieConsent</strong> do teu navegador, o que fará que se che solicite de novo a túa preferencia na próxima visita. Ten en conta que, se bloqueas algunhas cookies, certas funcións do sitio poden non estar dispoñibles. A continuación, indicámosche como xestionar as cookies nos navegadores máis habituais:",
        section6Links: [
          "<a href='https://support.google.com/chrome/answer/95647' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>Google Chrome</a>",
          "<a href='https://support.mozilla.org/gl/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>Mozilla Firefox</a>",
          "<a href='https://support.apple.com/es-es/guide/safari/sfri11471/mac' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>Safari</a>",
          "<a href='https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>Microsoft Edge</a>"
        ],
        section7Title: "Actualización desta política",
        section7Content: "Esta política de cookies pode actualizarse periodicamente para reflectir cambios nos servizos utilizados ou na normativa aplicable. Recomendámosche revisala de forma periódica. A data da última actualización indícase ao final desta páxina.",
        lastUpdated: "Última actualización: marzo de 2026",
      },
      avisoLegal: {
        title: "Aviso legal",
        subtitle: "Información legal sobre o uso deste sitio web e os seus contidos",
        intro: "En cumprimento do artigo 10 da Lei 34/2002, do 11 de xullo, de Servizos da Sociedade da Información e de Comercio Electrónico (LSSI-CE), ponse á disposición dos usuarios a seguinte información sobre o titular deste sitio web.",
        section0Title: "Datos do titular do sitio web",
        section0: [
          "Denominación: EGS (constitución societaria en proceso)",
          "NIF/CIF: pendente de asignación rexistral",
          "Domicilio: Santiago de Compostela, Galicia, España",
          "Correo electrónico: info@egswater.com",
          "Sitio web: https://egswater.com",
        ],
        section1Title: "Propiedade intelectual e industrial",
        section1Content: "Todos os contidos, logos, imaxes e textos publicados neste sitio web son propiedade de <strong>EGS</strong> ou conta coa licenza correspondente para o seu uso. Queda prohibida a súa reprodución total ou parcial, distribución, transformación ou comunicación pública sen autorización expresa e por escrito de EGS. As marcas, nomes comerciais ou signos distintivos son titularidade de EGS ou de terceiros que autorizaron o seu uso.",
        section2Title: "Responsabilidade",
        section2Content: "EGS non se fai responsable de erros, omisións ou danos derivados do uso da información proporcionada neste sitio ou empregada para outros fins distintos aos plantexados. Así mesmo, EGS non se responsabiliza dos contidos de sitios web externos enlazados desde este portal, cuxa xestión corresponde aos seus respectivos titulares. EGS non garante a dispoñibilidade ininterrompida do sitio web e resérvase o dereito a suspender ou interromper o acceso ao mesmo por razóns técnicas ou de mantemento.",
        section3Title: "Lexislación aplicable e xurisdición",
        section3Content: "As presentes condicións de uso réxense pola lexislación española, en particular pola LSSI-CE e demais normativa aplicable. Para a resolución de calquera controversia derivada do uso deste sitio web, as partes sométense aos xulgados e tribunais de Santiago de Compostela, con renuncia expresa a calquera outro foro que puidera corresponderlles.",
        section4Title: "Modificacións do aviso legal",
        section4Content: "EGS resérvase o dereito a modificar o presente aviso legal en calquera momento. Os cambios serán publicados nesta mesma páxina. Recomendamos consultar este aviso periodicamente para manterse informado de posibles actualizacións.",
        lastUpdated: "Última actualización: marzo de 2026",
      },
    },
    dashboard: {
      pageTitle: "Panel institucional | EGS",
      session: {
        loading: "Cargando",
        notStarted: "Sesión non iniciada",
        notStartedDesc: "Non iniciaches sesión ou a túa sesión caducou.",
        backToLogin: "Volver a Iniciar Sesión",
      },
      notifications: {
        types: {
          newFile: "Novo ficheiro subido",
          analysisComplete: "Análise completado",
          filePersisted: "Ficheiro persistido",
          analysisError: "Erro na análise",
          readingsParsed: "Parseo de lecturas completado",
          scoresCalculated: "Scores calculados",
          readingsError: "Erro no parseo de lecturas",
          scoresError: "Erro nos scores",
        },
        loading: "Cargando...",
        empty: "Sen notificacións",
      },
      menu: {
        resumen: "Resumo",
        cargar: "Cargar datos",
        mapa: "Prediccións de fuga",
        mapa2: "Consumo excesivo",
        riesgos: "Riscos",
        solicitudes: "Solicitudes",
        incidencias: "Incidencias",
        lecturas: "Lecturas",
        administracion: "Administración",
        informes: "Informes",
        configuracion: "Configuración",
      },
      sidebar: {
        title: "Panel institucional",
        logout: "Pechar sesión",
        logoutDialogTitle: "Pechar sesión",
        logoutDialogDesc: "Tes a certeza de que queres pechar sesión?",
        logoutCancel: "Cancelar",
        logoutConfirm: "Pechar sesión",
      },
      severity: {
        ok: "OK",
        watch: "Vixiar",
        urgent: "Inspección urxente",
        estimated: "estimación",
      },
      sections: {
        riesgos: {
          title: "Riscos",
          subtitle: "Fugas e consumo excesivo por vivenda",
          tabLeaks: "Fugas",
          tabConsumption: "Consumo excesivo",
          seccionLabel: "Sección",
          periodoLabel: "Período",
          selectSeccion: "Selecciona unha sección",
          selectPeriodo: "Selecciona un período",
          mapTitle: "Mapa de risco",
          togglePoints: "Puntos",
          toggleHeatmap: "Mapa de calor",
          panelTitle: "Detalle de vivenda",
          panelEmpty: "Selecciona unha vivenda no mapa ou na táboa",
          impactLabel: "Impacto estimado",
          searchHousing: "Buscar vivenda...",
          colHousing: "Vivenda",
          colPeriod: "Período",
          colProb: "Prob.",
          colImpact: "Impacto",
          colSeverity: "Estado",
          colCost: "Custo (est.)",
          pagPrev: "Anterior",
          pagNext: "Seguinte",
          pagShowing: "Amosando {a}–{b} de {n}",
          noData: "Non hai datos dispoñibles",
          selectToView: "Selecciona unha sección e un período para ver os scores",
          totalAnalyzed: "Total de vivendas analizadas:",
          legendOk: "OK (≤33%)",
          legendWatch: "Vixiar (34-66%)",
          legendUrgent: "Inspección urxente (>66%)",
          showing: "Mostrando:",
          createIncident: "Crear incidencia",
          requestInspection: "Solicitar inspección",
          toggleIncidents: "Incidencias ({n})",
          mapSizeHint: "O tamaño do punto reflicte o custo esperado; ①–⑤ marcan as maiores prioridades.",
          sortHint: "Ordenar por esta columna",
          sortedByCostNote: "Ordenada por custo esperado (probabilidade × consumo medio × tarifa), de maior a menor. Preme unha columna para reordenar.",
          anomaliesTitle: "Anomalías detectadas",
          noAnomalies: "Sen anomalías nas lecturas",
          histTitle: "Histórico de lecturas",
          seccionAll: "Ver todas as seccións",
          loadingSections: "Cargando sección {done} de {total}...",
          colSeccion: "Sección",
          legendSections: "Seccións",
          sectionsWithData: "{n} de {m} seccións con datos neste período",
          breakdownTitle: "Desagregación por sección",
          breakdownColSeccion: "Sección",
          breakdownColViviendas: "Vivendas",
          breakdownColUrgentes: "Urxentes",
          breakdownColImpacto: "Impacto",
          breakdownColCoste: "Custo (est.)",
          breakdownHint: "Preme unha sección para vela en detalle.",
        },
        mapa: {
          title: "Prediccións de fuga",
          subtitle: "Análise detallado de probabilidades de fuga por vivenda",
          seccionLabel: "Sección",
          periodoLabel: "Período",
          selectSeccion: "Selecciona unha sección",
          selectPeriodo: "Selecciona un período",
          probabilitiesSummary: "Resumo de probabilidades",
          low: "Baixa",
          medium: "Media",
          high: "Alta",
          totalAnalyzed: "Total de vivendas analizadas:",
          mapTitle: "Mapa de probabilidades de fuga",
          searchHousing: "Buscar vivenda...",
          colHousing: "Vivenda",
          colPeriod: "Período",
          colProb: "Prob.",
          noData: "Non hai datos dispoñibles",
          selectToView: "Selecciona unha sección e un período para ver os scores",
          probabilityTitle: "Probabilidade de fuga",
          tooltipPeriodo: "Período:",
          tooltipLeakProb: "Probabilidade de fuga:",
          tooltipRisk: "Nivel de risco:",
          legendLow: "Baixa probabilidade (≤33%)",
          legendMedium: "Media probabilidade (34-66%)",
          legendHigh: "Alta probabilidade (>66%)",
          showing: "Mostrando:",
        },
        mapa2: {
          title: "Consumo excesivo",
          subtitle: "Análise de vivendas con consumo anómalamente elevado",
          seccionLabel: "Sección",
          periodoLabel: "Período",
          selectSeccion: "Selecciona unha sección",
          selectPeriodo: "Selecciona un período",
          searchHousing: "Buscar vivenda...",
          colHousing: "Vivenda",
          colPeriod: "Período",
          colProb: "Prob.",
          noData: "Non hai datos dispoñibles",
          selectToView: "Selecciona unha sección e un período para ver os scores",
          probabilitiesSummary: "Resumo de consumo excesivo",
          mapTitle: "Mapa de consumo excesivo",
          excessTitle: "Probabilidade de consumo excesivo",
          tooltipPeriodo: "Período:",
          tooltipExcessProb: "Probabilidade de consumo excesivo:",
          tooltipRisk: "Nivel de risco:",
          low: "Baixa",
          medium: "Media",
          high: "Alta",
          totalAnalyzed: "Total de vivendas analizadas:",
          legendLow: "Baixa probabilidade (≤33%)",
          legendMedium: "Media probabilidade (34-66%)",
          legendHigh: "Alta probabilidade (>66%)",
          showing: "Mostrando:",
        },
        solicitudes: {
          title: "Solicitudes",
          subtitle: "Monitoriza e xestiona as túas solicitudes de análise",
          completed: "Completadas:",
          inProgress: "En proceso:",
          pending: "Pendentes:",
          activeFilter: "Filtro activo:",
          filterNone: "Ningún",
          statusCompleted: "Completado",
          statusInProgress: "En Proceso",
          statusPending: "Pendente",
        },
        incidencias: {
          title: "Incidencias",
          subtitle: "Incidencias reportadas polos cidadáns do municipio",
          total: "Total:",
          pending: "Pendentes:",
          completed: "Completadas:",
          noIncidents: "Non hai incidencias reportadas",
          pendingTitle: "Incidencias pendentes",
          completedTitle: "Incidencias completadas",
          viewImages: "Ver imaxes",
          markCompleted: "Marcar como completada",
          markPending: "Marcar como pendente",
        },
        lecturas: {
          title: "Lecturas",
          subtitle: "Rexistro de lecturas de consumo de todas as vivendas",
          searchPlaceholder: "Buscar por vía, número, bloque, planta, porta, sección, data...",
          addBtn: "Engadir lectura",
          modalTitle: "Nova lectura",
          housingLabel: "Vivenda",
          housingPH: "Selecciona unha vivenda",
          dateLabel: "Data de rexistro",
          counterLabel: "Lectura do contador (m³)",
          cancel: "Cancelar",
          saving: "Gardando...",
          save: "Gardar lectura",
          deleteTitle: "Eliminar lectura?",
          deleteDesc: "Esta acción non se pode desfacer.",
          deleteCancelBtn: "Cancelar",
          deleteConfirmBtn: "Eliminar",
          loadingText: "Cargando lecturas...",
          noResults: "Non se atoparon resultados",
          noReadings: "Non hai lecturas rexistradas",
          colVia: "Vía",
          colNum: "Nº",
          colBloque: "Bloque",
          colPlanta: "Planta",
          colPuerta: "Porta",
          colSeccion: "Sección",
          colLectura: "Lectura (m³)",
          colFecha: "Data",
          tabAnomalies: "Anomalías",
          tabAll: "Todas",
          anomalyZero: "Lectura en cero",
          anomalySpike: "Salto brusco",
          anomalyDrop: "Caída brusca",
          noAnomalies: "Non se detectaron anomalías neste conxunto de lecturas",
        },
        administracion: {
          title: "Administración",
          subtitle: "Xestiona os cidadáns e usuarios do sistema",
          searchCitizenPlaceholder: "Buscar cidadán...",
          searchHousingPlaceholder: "Buscar vivenda...",
          colEmail: "Email",
          colName: "Nome",
          colLastName: "Apelidos",
          colPhone: "Teléfono",
          namePlaceholder: "Ex: Juan",
          lastNamePlaceholder: "Ex: García López",
          emailPlaceholder: "Ex: usuario@exemplo.com",
          dniPlaceholder: "Ex: 12345678A",
          phonePlaceholder: "Ex: 600123456",
          registerTitle: "Rexistrar novo cidadán",
          registerDesc: "Crea unha conta para un cidadán do teu municipio",
          nombreLabel: "Nome *",
          apellidosLabel: "Apelidos *",
          emailLabel: "Email *",
          dniLabel: "DNI *",
          telefonoLabel: "Teléfono (opcional)",
          registering: "Rexistrando...",
          registerBtn: "Rexistrar cidadán",
          privacyNotice: "Os datos introducidos serán tratados conforme á nosa ",
          helpText: "Xerarase un contrasinal automático e enviarase un correo ao usuario con instrucións para cambialo.",
          pendingUsersTitle: "Usuarios pendentes de validación",
          pendingUsersDesc: "Cidadáns que solicitaron acceso e están esperando a túa aprobación",
          noPendingUsers: "Non hai usuarios pendentes de validación",
          allUsersTitle: "Todos os cidadáns",
          allUsersDesc: "Lista completa de cidadáns validados no teu municipio",
          noUsers: "Non hai cidadáns rexistrados",
        },
        configuracion: {
          title: "Configuración",
          subtitle: "Administra a túa conta e preferencias",
          changePassword: "Cambiar contrasinal",
          newPasswordLabel: "Novo contrasinal",
          confirmPasswordLabel: "Confirmar novo contrasinal",
          minPasswordPlaceholder: "Mínimo 6 caracteres",
          repeatPasswordPlaceholder: "Repite o contrasinal",
          updating: "Actualizando...",
          updatePassword: "Actualizar Contrasinal",
          notificationsTitle: "Notificacións",
          emailNotifications: "Notificacións por correo",
          emailNotificationsDesc: "Recibe actualizacións por correo electrónico",
          saveButton: "Gardar configuración",
          saving: "Gardando...",
          saveNotificationsBtn: "Gardar preferencias",
          accountTitle: "Información da conta",
          orgLabel: "Organización",
          emailLabel: "Email",
          roleLabel: "Rol",
          roleInstitution: "Institución",
          roleCitizen: "Cidadán",
          roleUnknown: "Descoñecido",
          tarifaTitle: "Tarifa da auga",
          tarifaDesc: "Prezo por m³ usado para estimar o impacto económico de fugas e consumo excesivo",
          tarifaLabel: "Prezo por m³ (€)",
          tarifaDecimalHint: "(o decimal escríbese con coma, ex. 1,80)",
          tarifaSaveBtn: "Gardar tarifa",
          tarifaSaved: "Tarifa gardada",
        },
        cargar: {
          title: "Cargar datos",
          subtitle: "Carga arquivos de datos para a súa análise",
        },
        resumen: {
          title: "Resumo",
          subtitle: "Visualiza as vivendas con maior e menor puntuación",
          loading: "Cargando scores...",
          excessTitle: "Maior probabilidade de consumo excesivo",
          leakTitle: "Maior probabilidade de fuga",
          period: "Período:",
          noData: "Non hai datos dispoñibles",
          seccionLabel: "Sección",
          periodoLabel: "Período",
          pendingRequestsTitle: "Solicitudes pendentes",
          pendingRequestsDesc: "Seguimento de procesos en curso",
          kpiWaterAtRisk: "Auga en risco",
          kpiHighRiskHomes: "Vivendas en risco alto",
          kpiOpenIncidents: "Incidencias abertas",
          kpiConsumptionChange: "Consumo vs período anterior",
          priorityTitle: "Prioridades de hoxe",
          prioritySubtitle: "Vivendas que máis impacto teñen agora mesmo",
          priorityEmpty: "Non hai vivendas prioritarias neste período",
          mapTitle: "Mapa de risco",
          kpiWaterAtRiskHint: "Suma do impacto estimado das {n} vivendas analizadas (fugas + consumo excesivo).",
        },
        informes: {
          title: "Informes",
          subtitle: "Xera informes imprimibles a partir dos datos do panel",
          seccionLabel: "Sección",
          periodoLabel: "Período",
          selectSeccion: "Selecciona unha sección",
          selectPeriodo: "Selecciona un período",
          templateLabel: "Modelo",
          templateTecnico: "Informe técnico de riscos",
          templateTecnicoDesc: "KPIs, puntos de actuación prioritarios, mapa e anexo de anomalías.",
          templateEficiencia: "Informe de eficiencia hídrica",
          templateEficienciaDesc: "Orientado a xustificación ante Augas de Galicia e subvencións de eficiencia hídrica.",
          generateBtn: "Xerar informe",
          selectToGenerate: "Selecciona unha sección e un período para xerar o informe",
          generatingBtn: "Xerando…",
          generatesInBackground: "O informe xérase en segundo plano e aparece na táboa ao rematar.",
          generatingHint: "Podes seguir usando o panel; o informe aparecerá na táboa ao rematar.",
          generatingToast: "Xerando o informe…",
          generatedOk: "Informe xerado e gardado.",
          generateError: "Non se puido xerar o informe.",
          savedTitle: "Informes xerados",
          savedSubtitle: "Cada informe gárdase ao xerarse. Ábreo para editar os textos ou descargar o PDF.",
          colTitulo: "Informe",
          colAmbito: "Ámbito",
          colPeriodo: "Período",
          colPlantilla: "Modelo",
          colCreado: "Xerado",
          colAcciones: "Accións",
          ambitoConcello: "Todo o concello",
          openBtn: "Abrir",
          deleteBtn: "Borrar",
          confirmDelete: "Seguro que queres borrar este informe? Non se pode desfacer.",
          deletedOk: "Informe borrado.",
          deleteError: "Non se puido borrar o informe.",
          listError: "Non se puideron cargar os informes gardados.",
          emptyList: "Aínda non xeraches ningún informe.",
          loadingList: "Cargando informes…",
          refresh: "Actualizar",
          prev: "Anterior",
          next: "Seguinte",
          pageOf: "de",
        },
      },
      informe: {
        downloadBtn: "Descargar PDF",
        generatingBtn: "Xerando…",
        back: "Volver ao panel",
        loading: "Cargando datos do informe...",
        error: "Non se puideron cargar os datos do informe.",
        generatedOn: "Xerado o",
        municipality: "Concello",
        section: "Sección",
        period: "Período",
        estimateDisclaimer: "As cifras de m³ e € son estimacións (probabilidade × consumo medio × tarifa) e non substitúen unha inspección en campo.",
        editableHint: "(preme para editar)",
        techTitle: "Informe técnico de riscos de auga",
        summaryHeading: "Resumo executivo",
        summaryPlaceholder: "Escribe aquí un resumo executivo do período: achados principais, vivendas críticas e recomendación de actuación.",
        seccionesHeading: "Seccións por prioridade",
        allSecciones: "Todas as seccións",
        generateAI: "Xerar con IA",
        generatingAI: "Xerando…",
        aiError: "Non se puido xerar o resumo. Téntao de novo.",
        aiSessionError: "A túa sesión caducou. Volve iniciar sesión para xerar o resumo.",
        aiDisclaimer: "Texto xerado automaticamente a partir dos datos do período. Revísao e edítao antes de publicar o informe.",
        kpiHeading: "Indicadores do período",
        kpiWater: "Auga en risco (estimada)",
        kpiHighRisk: "Vivendas en risco alto",
        kpiIncidents: "Incidencias abertas",
        kpiTotal: "Vivendas analizadas",
        topHeading: "Puntos de actuación prioritarios (top 10)",
        colHousing: "Vivenda",
        colProb: "Prob.",
        colImpact: "Impacto",
        colCost: "Custo (est.)",
        colStatus: "Estado",
        mapHeading: "Mapa de risco",
        anomaliesHeading: "Anexo: anomalías detectadas nas lecturas",
        anomalyColHousing: "Vivenda",
        anomalyColType: "Anomalía",
        anomalyColDate: "Data",
        noAnomalies: "Non se detectaron anomalías no período.",
        observationsHeading: "Observacións do técnico",
        observationsPlaceholder: "Anota observacións de campo, decisións tomadas e próximos pasos.",
        effTitle: "Informe de eficiencia hídrica",
        effSubtitle: "Documento de apoio para xustificación ante Augas de Galicia e solicitudes de subvención de eficiencia hídrica",
        lossesHeading: "Volume estimado de perdas e custo asociado",
        lossesDesc: "Estimación agregada da auga en risco por fugas e consumo excesivo no período analizado.",
        statusBreakdownHeading: "Distribución de vivendas por estado",
        prioritiesHeading: "Relación de puntos de actuación prioritarios",
        manualDataHeading: "Datos de rede (cumprimentación manual)",
        injectedLabel: "Volume de auga inxectada na rede (m³)",
        anrLabel: "Auga non rexistrada / non facturada (m³)",
        iliLabel: "Índice de perdas (ILI ou outro indicador)",
        manualHint: "Estes datos aínda non se calculan automaticamente; complétaos manualmente ou consulta o TODO do proxecto.",
        actionsHeading: "Actuacións realizadas",
        actionsPlaceholder: "Describe as actuacións executadas: inspeccións, reparacións, substitución de contadores, campañas de sensibilización...",
        measuresHeading: "Medidas previstas",
        measuresPlaceholder: "Describe as medidas previstas e o calendario estimado.",
        saveBtn: "Gardar cambios",
        retrySave: "Reintentar gardado",
        saving: "Gardando…",
        saved: "Gardado",
        unsaved: "Cambios sen gardar",
        savedOk: "Informe gardado.",
        saveError: "Non se puido gardar o informe.",
      },
      upload: {
        title: "Cargar arquivo de datos",
        subtitle: "Sube un arquivo cos datos da solicitude",
        fileLabel: "Arquivo",
        formats: "📁 Formatos: CSV, XLSX.",
        uploadBtn: "Subir",
        selectFile: "Escoller arquivo",
        noFileSelected: "Ningún arquivo seleccionado",
        privacyNotice: "Ao subir este arquivo confirmas que o seu tratamento cumpre coa nosa ",
        privacyLink: "Política de Privacidade",
      },
      imageModal: {
        title: "Imaxes de incidencia",
      },
      messages: {
        incidentCompleteError: "Erro ao marcar a incidencia como completada. Por favor, inténtao de novo.",
        incidentPendingError: "Erro ao marcar a incidencia como pendente. Por favor, inténtao de novo.",
        userRegistered: "Usuario rexistrado correctamente. Enviarase un email coas instrucións.",
        userDeleted: "Usuario eliminado correctamente",
        userDeleteError: "Erro ao eliminar usuario",
        confirmDeleteUserPrefix: "Tes a certeza de que desexas eliminar permanentemente ao usuario",
        confirmDeleteUserSuffix: "?\n\nEsta acción non se pode desfacer e eliminará todos os seus datos.",
        confirmIncidentPending: "Tes a certeza de que queres marcar esta incidencia como pendente?",
        addressUnavailable: "Dirección non dispoñible",
      },
    },
    ciudadano: {
      pageTitle: "Panel persoal | EGS",
      privacyFooter: "Os datos que introduces nesta aplicación son tratados conforme á nosa ",
      session: {
        loading: "Cargando",
        notStarted: "Sesión non iniciada",
        notStartedDesc: "Non iniciaches sesión ou a túa sesión caducou.",
        backToLogin: "Volver a Iniciar Sesión",
      },
      menu: {
        inicio: "Inicio",
        addVivienda: "Engadir vivenda",
        consultarViviendas: "Consultar vivendas",
        addLectura: "Engadir lectura",
        consultarLecturas: "Consultar lecturas",
        addIncidencia: "Engadir incidencia",
        consultarIncidencias: "Consultar incidencias",
        configuracion: "Configuración",
      },
      sidebar: {
        logout: "Pechar sesión",
        logoutDialogTitle: "Pechar sesión",
        logoutDialogDesc: "Tes a certeza de que queres pechar sesión?",
        logoutCancel: "Cancelar",
        logoutConfirm: "Pechar sesión",
      },
      validation: {
        fillAllFields: "❌ Debes completar todos os campos",
        passwordNoMatch: "❌ Os contrasinais non coinciden",
        passwordTooShort: "❌ O contrasinal debe ter polo menos 6 caracteres",
        passwordUpdated: "✅ Contrasinal actualizado. Redirixindo ao login...",
        serverError: "O servidor non devolveu unha resposta válida",
      },
      messages: {
        incidentDeleteError: "Erro ao eliminar a incidencia. Por favor, inténtao de novo.",
        notifLoadError: "Non se puideron cargar as preferencias de notificación",
        notifSaveError: "Non se puideron gardar as preferencias",
        unknownMunicipality: "Concello descoñecido",
      },
      sections: {
        inicio: {
          title: "Inicio",
          welcome: "Benvido/a, ",
          addHousingTitle: "Engadir vivenda",
          addHousingDesc: "Rexistra unha nova vivenda",
          viewHousingTitle: "Consultar vivendas",
          addReadingTitle: "Engadir lectura",
          addReadingDesc: "Rexistra lecturas do contador",
        },
        addVivienda: {
          title: "Engadir vivenda",
          subtitle: "Rexistra unha nova vivenda na túa conta",
          formTitle: "Nova vivenda",
          viaLabel: "Vía",
          viaPH: "Seleccione unha vía",
          numeroLabel: "Número",
          numeroPH: "Seleccione un número",
          noNumero: "Sen número",
          bloqueLabel: "Bloque",
          bloquePH: "Seleccione un bloque",
          noBloque: "Sen bloque",
          plantaLabel: "Planta",
          plantaPH: "Seleccione unha planta",
          noPlanta: "Sen planta",
          puertaLabel: "Porta",
          puertaPH: "Seleccione unha porta",
          noPuerta: "Sen porta",
          cancel: "Cancelar",
          save: "Gardar vivenda",
          cusecInfoBefore: "ℹ️ O código CUSEC asignarase automaticamente como",
          cusecDesc: "(Concello de Cerdedo)",
        },
        consultarViviendas: {
          title: "Consultar vivendas",
          subtitle: "Vivendas rexistradas",
          tableTitle: "Vivendas rexistradas",
          searchPlaceholder: "Buscar vivenda...",
          noHousing: "Non hai vivendas rexistradas",
          addFirstHousing: "Engadir primeira vivenda",
          noResults: "Non se atoparon vivendas que coincidan coa busca",
          colVivienda: "Vivenda",
          colPeriod: "Período",
          colType: "Tipo",
          colProb: "Prob.",
          colRiesgo: "Risco",
          colAcciones: "Accións",
          leakProb: "Probabilidade de fuga",
          excessConsumption: "Consumo excesivo",
          noData: "Sen datos",
          pending: "⏳ Pendente",
          deleteHousing: "Eliminar vivenda",
          period: "Período:",
          scoreLabel: "Score:",
          riskLabel: "Risco:",
          riskLow: "Baixo",
          riskMedium: "Medio",
          riskHigh: "Alto",
        },
        addLectura: {
          title: "Engadir lectura",
          subtitle: "Rexistra unha nova lectura do contador",
          formTitle: "Nova lectura",
          noHousing: "Non hai vivendas rexistradas",
          addHousingFirst: "Engadir vivenda primeiro",
          housingLabel: "Vivenda",
          housingPH: "Selecciona unha vivenda",
          dateLabel: "Data de rexistro",
          counterLabel: "Lectura do contador",
          cancel: "Cancelar",
          save: "Gardar lectura",
        },
        consultarLecturas: {
          title: "Consultar lecturas",
          subtitle: "Lecturas rexistradas",
          tableTitle: "Lecturas rexistradas",
          searchPlaceholder: "Buscar por vivenda...",
          noReadings: "Non hai lecturas rexistradas",
          addReading: "Engadir lectura",
          noResults: "Non se atoparon lecturas que coincidan coa busca",
          colVivienda: "Vivenda",
          colFecha: "Data",
          colLectura: "Lectura",
          viviendaLabel: "Vivenda:",
          fechaLabel: "Data:",
          lecturaLabel: "Lectura:",
        },
        addIncidencia: {
          title: "Engadir incidencia",
          subtitle: "Reporta unha nova incidencia",
          formTitle: "Nova incidencia",
          noHousing: "Non hai vivendas rexistradas",
          addHousingFirst: "Engadir vivenda primeiro",
          housingLabel: "Vivenda",
          housingPH: "Selecciona unha vivenda",
          subjectLabel: "Asunto",
          subjectPH: "Escribe un resumo breve",
          descLabel: "Descrición",
          descPH: "Aquí podes estenderte máis",
          imagesLabel: "Imaxes (opcional, máximo 2)",
          addImage: "Tirar foto ou seleccionar imaxe",
          addAnotherImage: "Engadir outra imaxe",
          cancel: "Cancelar",
          save: "Gardar incidencia",
        },
        consultarIncidencias: {
          title: "Consultar incidencias",
          subtitle: "Incidencias gardadas",
          total: "Total:",
          pending: "Pendentes:",
          completed: "Completadas:",
          noIncidents: "Non hai incidencias gardadas",
          noIncidentsDesc: "Comeza reportando a túa primeira incidencia",
          addIncident: "Engadir incidencia",
          pendingTitle: "Incidencias pendentes",
          completedTitle: "Incidencias completadas",
          subjectLabel: "Asunto",
          descLabel: "Descrición",
          viewImages: "Ver imaxes",
          pendingBadge: "Pendente",
          completedBadge: "Completada",
        },
        configuracion: {
          title: "Configuración",
          subtitle: "Administra a túa conta e preferencias",
          changePassword: "Cambiar contrasinal",
          newPasswordLabel: "Novo contrasinal",
          confirmPasswordLabel: "Confirmar novo contrasinal",
          minPasswordPlaceholder: "Mínimo 6 caracteres",
          repeatPasswordPlaceholder: "Repite o contrasinal",
          updating: "Actualizando...",
          updatePassword: "Actualizar Contrasinal",
          notificationsTitle: "Notificacións por correo",
          notificationsDesc: "Recibe actualizacións por correo electrónico",
          saveNotifications: "Gardar",
          notificationsHeader: "Notificacións",
          saving: "Gardando...",
          saveNotificationsBtn: "Gardar preferencias",
          accountTitle: "Información da conta",
          orgLabel: "Organización",
          emailLabel: "Correo electrónico",
          roleLabel: "Rol",
        },
      },
    },
  },

  en: {
    // Header
    nav: {
      whatHappens: "Challenge",
      ourSolution: "Product",
      meetUs: "Meet EGS",
      join: "Contact",
      login: "Log In",
      startFree: "Start for free",
      news: "News",
    },

    // Hero Section
    hero: {
      badge: "Finalists of the Bankinter Innovation Program",
      badgeLine1: "Finalists of the Bankinter",
      badgeLine2: "Innovation Program",
      title: "Reduce leaks.",
      titleHighlight: "Digitalize your water management.",
      subtitle: "The management software for municipalities that predicts where to act against water leaks, monitors consumption and connects citizens. Goodbye to chaos. Hello EGS.",
      ctaPrimary: "Start for free",
      newsTicker: "Mention at the AJE Pontevedra 2026 Awards",
      ctaSecondary: "Our Team",
    },

    // Logo Wall
    logos: {
      heading: "Originated in",
    },

    // Global Water Crisis Section
    crisis: {
      title: "EGS responds to the global water crisis",
      subtitle: "There is alarming data that demonstrates the urgency to act now:",
      stat1: "of Earth's water is suitable for human consumption",
      stat2: "people could lack drinking water by 2050",
      stat3: "spent per person per day. Reducing to 50 L would break the cycle of poverty",
      trendsTitle: "Two trends worsen the water crisis",
      trend1Title: "Population Growth",
      trend1Text: "The world population will reach 9.7 billion people by 2050. Population growth leads to increased demand for drinking water. According to the UN, 1.8 billion people will live in areas lacking drinking water.",
      trend2Title: "Climate Change",
      trend2Text: "Rising temperatures and altered rainfall patterns drastically reduce freshwater availability. According to the WHO, 20% of the world's population lacks regular access to drinking water and sanitation, leading to the spread of diseases.",
    },

    // Galicia Section
    galicia: {
      title: "Galicia is no exception",
      stat1: "Region that loses the most water through leaks in its network, according to S&P Global Ratings",
      stat2: "daily liters are recoverable with leak repairs",
      stat3Only: "only",
      stat3: "of municipalities prevent leaks despite drought risk",
      closing: "Galicia and Asturias are the regions with the highest water leaks in their networks. Murcia, Andalusia and the Balearic Islands face the greatest water stress.",
      closingHighlight1: "Galicia and Asturias",
      closingHighlight2: "Murcia, Andalusia and the Balearic Islands",
    },

    // Solution Section
    solution: {
      badge: "For municipalities and institutions",
      title: "Our Solution",
      subtitle: "Comprehensive system for water cycle management",
      description: "We offer a web management system that starts from water record files to provide centralized data, grant total control and favor smarter decisions to institutions.",
      comingSoon: "Coming Soon",
      comingSoonTitle: "Technology at the Service of Water",
      comingSoonSubtitle: "Predictions, alerts and digitized metrics to reduce leaks and water waste",
      scrollTitle: "Get a comprehensive system for",
      scrollSubtitle: "Water Management",
      systemIntro: "We control and reduce unregistered water consumption through leak detection and spending pattern analysis. The system enables",
      systemTarget: "municipalities and institutions",
      systemAllows: " to:",
      feature1Bullet: "Obtain a <strong>prioritized map</strong> of <strong>action points</strong>, through predictions and digitized alerts",
      feature2Bullet: "Calculate estimated cost overruns from losses",
      feature3Bullet: "Report meter readings and citizen incidents",
      description2: "We divide the service into 3 main layers",
      stepsTitle: "How EGS works",
      feature1Title: "Predictive AI",
      feature1Text: "Algorithms capable of anticipating problems, predicting and alerting before they occur to optimize the maintenance of water systems.",
      feature2Title: "Advanced Analysis",
      feature2Text: "Real-time dashboards that analyze consumption patterns, predict resource demand and estimate associated costs in detail.",
      feature3Title: "Complete Management",
      feature3Text: "Proposal of sustainable solutions to detected leaks or vulnerabilities, ensuring more efficient and responsible water management.",
      videoHeadline1: "Total control for your municipality",
      videoHeadline2: "Water, in the citizen's hands",
      videoPill1_1: "Leak map",
      videoPill1_2: "Predictive alerts",
      videoPill1_3: "Technical management",
      videoPill2_1: "Real-time consumption",
      videoPill2_2: "Incident reporting",
    },

    // About Us Section
    about: {
      title: "Meet EGS",
      intro: "At EGS, we are a multidisciplinary team of Galician entrepreneurs with a clear mission: to transform water management globally, starting from our cities, towns and villages, but with the ambition to make a difference on a large scale.",
      missionTitle: "Our Mission",
      missionText: "Guarantee universal access to clean water through purposeful technology and intelligent management.",
      purposeTitle: "Our Purpose",
      purposeText: "Create a global movement that transforms water management for future generations.",
      keyTitle: "Our Key",
      keyText: "To achieve lasting impact, we combine collaboration with shared knowledge.",
      teamIntro: "An organization is built from its people. Behind EGS is a diverse team passionate about what they do and the impact they seek to generate.",
      luis: {
        name: "Luis Encinas",
        role: "Institutional Relations and Finance",
        bio: "Entrepreneur with strong social commitment and transformative vision of technological impact.",
      },
      ignacio: {
        name: "Ignacio Garbayo",
        role: "Development and Artificial Intelligence",
        bio: "Brings to life what he develops with the conviction that technology will transform water management.",
      },
      martin: {
        name: "Martín Sende",
        role: "Management and Sustainability Solutions",
        bio: "Passionate about science and convinced of the real impact of technology on natural resources.",
      },
      viewProfile: "View Profile",
    },

    // FAQ Section
    faq: {
      title: "Frequently asked questions",
      subtitle: "Answers to the most common questions about EGS and how we work with municipalities.",
      items: [
        {
          question: "Do we need to install sensors or replace meters?",
          answer: "No. EGS works with the meter readings your municipality already collects; no hardware installation or replacement is needed to get started. We adapt to your existing model.",
        },
        {
          question: "Who owns the data and how is privacy protected?",
          answer: "The data always belongs to the municipality; EGS only processes it to deliver the service, in compliance with GDPR and hosted within the EU.",
        },
        {
          question: "What does the pilot consist of?",
          answer: "It's free. The municipality only provides a history of readings; at the end it receives a prioritised map of potential leaks, with no commitment required.",
        },
        {
          question: "How can a public administration contract EGS?",
          answer: "After the pilot, EGS can be contracted through a minor contract, subscription, or integrated into a tender, with support throughout the entire process.",
        },
        {
          question: "Who is behind EGS?",
          answer: "A Galician team born at USC, backed by the Bankinter Innovation Foundation and the Fundación Princesa de Girona.",
        },
      ],
      knowUsLinkIntro: "Learn more in our",
      knowUsLinkText: "About Us",
      linkedinLinkIntro: "or on our",
      linkedinLinkText: "LinkedIn",
    },

    // Join Section
    join: {
      title: "Join us",
      subtitle: "Be part of the change. We seek institutions and collaborators looking to transform water management and create a more sustainable future for the next generations.",
      whyTitle: "Start now with EGS",
      impactTitle: "Impact",
      impactText: "Detect leaks before they become failures. Lower costs, more water saved.",
      commitmentTitle: "Commitment",
      commitmentText: "We integrate with your existing infrastructure. No construction, no additional hardware.",
      techTitle: "Technology",
      techText: "Real-time dashboards with automatic alerts. Manage your entire network from a single screen.",
      contactTitle: "Contact directly",
      formTitle: "Start Your Adventure",
      formSubtitle: "We will contact you as soon as possible.",
      formName: "Name",
      formLastName: "Last Name",
      formEmail: "Email",
      formCollaboration: "Role in the organization",
      formCollaborationPlaceholder: "Select an option",
      formCollaborationOptions: {
        partner: "Water Technician",
        client: "Infrastructure Technician",
        investor: "Municipal Services Manager",
        talent: "Mayor / Councillor / Official",
        other: "Other role",
      },
      formMessage: "Tell us about your municipality",
      formMessagePlaceholder: "Organization or council, approximate population (inhabitants), contact phone, and current network situation...",
      formPrivacy: "I accept the",
      formPrivacyLink: "privacy policy",
      formSubmit: "Request a pilot",
      formSubmitting: "Sending...",
      formSuccessMessage: "Thank you! We have received your request. We will contact you soon.",
      formErrorPrivacy: "You must accept the privacy policy",
      formErrorRequired: "Please complete all required fields",
      formErrorGeneral: "There was an error sending your request. Please try again.",
    },

    // Success Modal
    successModal: {
      title: "Request received",
      message: "Your request has been registered successfully. Our team will contact you within 24 to 48 hours.",
      button: "Close",
    },

    loadingModal: {
      message: "Please wait...",
      videoLoading: "Loading..."
    },

    // Welcome Email
    welcomeEmail: {
      subject: "Welcome to EGS - Registration confirmation",
      greeting: "Hello",
      title: "Welcome to EGS",
      message: "Thank you for registering on our intelligent water management platform.",
      received: "We have successfully received your information and it is being processed.",
      pending: "Your request is pending validation by your institution.",
      nextSteps: "What's next:",
      step1: "Our team will review your information",
      step2: "Your institution will validate your access",
      step3: "You will receive an email when your account is active",
      timeframe: "This process usually takes 24-48 hours.",
      questions: "Have any questions?",
      contactUs: "Feel free to contact us at",
      thanks: "Thank you for joining the water revolution",
      footer: "EGS",
      footerTagline: "From Galicia, we transform water management.",
    },

    // Password Change Page
    passwordChange: {
      title: "Change Password",
      subtitle: "Set up your new access password",
      newPassword: "New password",
      confirmPassword: "Confirm password",
      submit: "Change password",
      success: "Password changed successfully. Redirecting to login...",
      errorMinLength: "Password must be at least 8 characters long",
      errorMismatch: "Passwords do not match",
      errorInvalidToken: "The link is invalid or has expired",
      errorAlreadyUsed: "This link has already been used",
      errorGeneral: "Error changing password. Please try again.",
      loading: "Validating link...",
    },

    // Credentials Email
    credentialsEmail: {
      subject: "Your EGS account has been created",
      title: "Account created",
      greeting: "Hello",
      message: "Your account on the EGS platform has been created by your municipality.",
      instructions: "To start using your account, you need to set your password.",
      buttonText: "Change my password",
      note: "This link is valid for 24 hours.",
      footer: "If you didn't request this account, please contact your municipality.",
      privacyNotice: "Your personal data is processed by EGS in accordance with the General Data Protection Regulation (GDPR). You can review our Privacy Policy at: ",
    },

    // Footer
    footer: {
      description: "From Galicia, we transform water management through artificial intelligence and process automation to create a more sustainable future.",
      backedBy: "Backed by",
      contact: "Contact",
      quickLinks: "Quick Links",
      home: "Home",
      copyright: "© 2026 EGS. All rights reserved.",
      privacy: "Privacy Policy",
      legal: "Legal Notice",
      cookies: "Cookie Policy",
    },

    // News (LinkedIn posts) — /{lang}/news
    news: {
      metaTitle: "News | EGS",
      metaDescription: "EGS's latest LinkedIn posts: water, municipalities and technology in Galicia.",
      eyebrow: "From our LinkedIn page",
      title: "News",
      subtitle: "What we share about water, municipalities and technology in Galicia. Every post links to the original.",
      originalLanguage: "Posts are shown in the language they were written in on LinkedIn.",
      follow: "Follow EGS on LinkedIn",
      featured: "Featured",
      readPost: "Read post",
      allPosts: "All posts",
      sortedNote: "Newest first",
      readMore: "Read more",
      showMore: "Show more posts",
      empty: "No posts yet. In the meantime, you can follow us on LinkedIn.",
      ctaTitle: "Don't miss what's next",
      ctaText: "Follow us on LinkedIn to see every update the day we post it.",
      ctaButton: "Follow on LinkedIn",
      back: "Back to news",
      publishedOn: "Posted on LinkedIn",
      reactions: "reactions",
      comments: "comments",
      viewOnLinkedIn: "View on LinkedIn",
      copyLink: "Copy link",
      linkCopied: "Link copied",
      morePosts: "More posts",
      seeAll: "See all",
      image: "Image",
      shared: "Shared",
      sharedBy: "Shared by EGS",
      sharedFrom: "Original post by",
      linkedinPost: "LinkedIn post",
    },

    // Login Page
    loginPage: {
      title: "Log In",
      subtitle: "Access your EGS account",
      welcomeBack: "Welcome back",
      welcomeDescription: "Log in to access your control panel",
      email: "Email",
      emailPlaceholder: "your@email.com",
      password: "Password",
      passwordPlaceholder: "••••••••",
      loginButton: "Log In",
      loggingIn: "Logging in...",
      errorCredentials: "Incorrect credentials",
      errorGeneral: "Error logging in",
      errorAccountNotValidated: "Your account has not yet been validated by the administrator. Please wait for your registration to be approved.",
      noAccount: "Don't have an account?",
      signUpLink: "Sign up",
      orDivider: "or",
      googleLogin: "Continue with Google",
      googleLoggingIn: "Connecting with Google...",
      errorGoogle: "Error logging in with Google",
    },

    // Sign Up Page
    signupPage: {
      title: "Sign Up",
      subtitle: "Create your EGS account",
      welcome: "Join EGS",
      welcomeDescription: "Complete the form to request access",
      dni: "DNI",
      dniPlaceholder: "12345678A",
      nombre: "Name",
      nombrePlaceholder: "Your name",
      apellidos: "Last Name",
      apellidosPlaceholder: "Your last name",
      email: "Email",
      emailPlaceholder: "your@email.com",
      password: "Password",
      passwordPlaceholder: "••••••••",
      confirmPassword: "Confirm Password",
      confirmPasswordPlaceholder: "••••••••",
      telefono: "Phone (optional)",
      telefonoPlaceholder: "612345678",
      concello: "Municipality",
      concelloPlaceholder: "Select your municipality",
      notifications: "Receive email notifications",
      notificationsHelper: "Only for alerts about your properties. Account informational emails will be sent automatically.",
      signupButton: "Sign Up",
      signingUp: "Signing up...",
      haveAccount: "Already have an account?",
      loginLink: "Log in",
      errorDniFormat: "Invalid DNI format (8 digits + letter)",
      errorDniRequired: "DNI is required",
      errorNombreRequired: "Name is required",
      errorApellidosRequired: "Last name is required",
      errorEmailFormat: "Invalid email format",
      errorEmailRequired: "Email is required",
      errorPasswordRequired: "Password is required",
      errorPasswordLength: "Password must be at least 8 characters",
      errorPasswordMismatch: "Passwords do not match",
      errorConcelloRequired: "You must select a municipality",
      errorGeneral: "Error signing up. Please try again.",
      errorLoadingConcellos: "Error loading municipalities",
      googleSignup: "Sign up with Google",
      googleSignupDescription: "Your name and email will be automatically filled from your Google account. You will need to complete the remaining fields.",
      orDivider: "or",
      privacyCheckbox: "I have read and accept the ",
      privacyLink: "Privacy Policy",
      errorPrivacyRequired: "You must accept the privacy policy to register",
    },

    // Sign Up Success Page
    signupSuccess: {
      title: "Request Submitted",
      subtitle: "Your registration is pending approval",
      message: "Your request has been sent to your institution",
      description: "You will receive an email notification when your profile is approved and you can access the platform.",
      emailNote: "Important Note",
      emailNoteText: "The notification permission you granted during registration refers only to alerts about your properties. Informational emails related to your account will be sent automatically upon registration.",
      spamNote: "Don't see the email?",
      spamNoteText: "If you don't find the welcome email in your inbox, please check your spam or promotions folder. Please mark our emails as 'Not spam' to receive future notifications.",
      backToHome: "Back to home",
      goToLogin: "Go to log in",
    },

    // Legal Pages
    legal: {
      privacy: {
        title: "Privacy Policy",
        subtitle: "Information about how EGS collects, uses and protects your personal data",
        intro: "At <strong>EGS</strong> we value the privacy of our users, owners, municipalities and all people whose data we process. We comply with Regulation (EU) 2016/679 (GDPR) and with Organic Law 3/2018, on the Protection of Personal Data and guarantee of digital rights (LOPDGDD).",
        section1Title: "1. Data Controller",
        section1Content: ["Name: EGS (corporate incorporation in progress)", "Address: Galicia, Spain (registered address pending)", "Contact: info@egswater.com"],
        section2Title: "2. Data we process",
        section2Content: "We process data such as name, surname, address, identification and contact data, housing data, cadastral data, water consumption, census and owner data, among others. Some of this data is provided directly by the municipalities with whom EGS has a contractual relationship; in those cases EGS acts as data processor in accordance with Art. 28 GDPR.",
        section3Title: "3. Purposes and legal basis",
        section3Content: [
          { purpose: "Management of relationships with municipalities and owners", basis: "Art. 6.1.b GDPR — performance of contract" },
          { purpose: "Studies, audits and services on water consumption", basis: "Art. 6.1.b / Art. 6.1.e GDPR — performance of contract / public interest" },
          { purpose: "Maintenance of housing census", basis: "Art. 6.1.c / Art. 6.1.e GDPR — legal obligation / public interest" },
          { purpose: "Billing and collection management", basis: "Art. 6.1.b GDPR — performance of contract" },
          { purpose: "Compliance with legal and administrative obligations", basis: "Art. 6.1.c GDPR — legal obligation" },
          { purpose: "User notifications and communications (with their consent)", basis: "Art. 6.1.a GDPR — consent of the data subject" },
        ],
        section4Title: "4. Data retention",
        section4Content: "Data will be retained for the following periods: user account data, 3 years from deactivation; consumption and audit data, 5 years from the end of the contract with the municipality; billing data, 6 years in accordance with applicable commercial law. In case of litigation or claim, data will be retained until final resolution.",
        section5Title: "5. Recipients",
        section5Content: "The data may be communicated to municipalities, collaborating entities, service providers (data processors) and competent authorities, always guaranteeing compliance with the regulations.",
        section6Title: "6. International transfers",
        section6Content: "EGS works with technology service providers (cloud infrastructure, email delivery) that may operate partially outside the European Economic Area. In such cases, EGS requires these providers to execute Standard Contractual Clauses (SCCs) approved by the European Commission, or other adequate safeguards in accordance with Chapter V of the GDPR.",
        section7Title: "7. Data subject rights",
        section7Intro: "The user can exercise the following rights:",
        section7Content: [
          "Access their data",
          "Rectify or delete them",
          "Limit their processing",
          "Object to processing",
          "Request portability",
          "Withdraw their consent at any time",
          "File a complaint with the Spanish Data Protection Agency (AEPD)"
        ],
        section7LinkText: "Spanish Data Protection Agency (AEPD)",
        section7Exercise: "To exercise any of these rights, send an email to info@egswater.com indicating the right you wish to exercise and attaching a copy of your identity document. EGS will respond within a maximum of 1 month, extendable by a further 2 months in cases of particular complexity (Art. 12.3 GDPR).",
        section8Title: "8. Automated processing and artificial intelligence",
        section8Content: "EGS uses predictive analysis algorithms for leak detection and water consumption optimization. These analyses are applied to aggregate consumption data and do not produce individual decisions with significant legal effects on individuals. No automated decisions are made within the meaning of Art. 22 GDPR.",
        section9Title: "9. Data Protection Officer",
        section9Content: "EGS has assessed the obligation to appoint a Data Protection Officer (DPO) in accordance with Art. 37 GDPR and concludes that, in its current operational phase, the conditions that would make it mandatory are not met. This assessment will be reviewed periodically as the volume and typology of data processed grows.",
        section10Title: "10. Minors",
        section10Content: "EGS's service is directed exclusively at persons over 14 years of age, in accordance with Art. 7 of the LOPDGDD. EGS does not knowingly collect data from minors below this age. If it is detected that a registered user is under 14 years of age, their data will be deleted immediately.",
        section11Title: "11. Security measures",
        section11Content: "We have adopted technical and organizational measures to protect data against unauthorized access, loss, alteration or improper disclosure, including access control, secure authentication, encryption and internal security policies.",
        section12Title: "12. Security breaches",
        section12Content: "In the event of a security breach affecting personal data, we will notify the AEPD and, if applicable, also those affected, as soon as possible.",
        section13Title: "13. Cookie policy",
        section13Content: "The use of cookies and tracking technologies on this website is detailed in our Cookie Policy, available in the footer.",
        section14Title: "14. Modifications",
        section14Content: "This policy may be updated to adapt to legal or internal changes. In case of significant changes, notification will be provided through the website or other appropriate means.",
        lastUpdated: "Last updated: March 2026",
      },
      cookies: {
        title: "Cookie Policy",
        subtitle: "Information about the use of cookies on this website",
        intro: "This website uses <strong>cookies</strong> to improve the user experience, analyze traffic and personalize content according to your preferences. Below, we explain in detail which cookies we use, their purpose and how you can manage them.",
        section1Title: "What are cookies?",
        section1Content: [
          "Cookies are small text files that websites store in your browser when you visit them. They are used to remember your preferences, facilitate navigation and collect statistical information about site usage."
        ],
        section2Title: "Types of cookies we use",
        section2Content: "Based on their purpose, we classify the cookies on this site into the following categories:",
        section3Title: "Strictly necessary cookies",
        section3Content: [
          "These cookies are essential for the basic functioning of the website and cannot be disabled. They are used to manage user authentication, maintain an active session and remember language preferences.",
          "<strong>access_token / refresh_token:</strong> Manage the authenticated user's session. They are generated upon login and are necessary to access protected areas (control panel). Duration: session (deleted upon logout or token expiry).",
          "<strong>lang</strong> (cookie, 12 months): Remembers the language selected by the user to provide a consistent experience between visits.",
          "<strong>cookieConsent</strong> (cookie, 12 months): Stores your choice about accepting or rejecting cookies. It expires automatically after 12 months, at which point you will be asked for your preference again."
        ],
        section4Title: "Analytics and performance cookies",
        section4Content: [
          "These cookies allow us to analyze how users interact with the website, helping us improve its performance and content.",
          "<strong>Vercel Analytics:</strong> We use Vercel's analytics service, which collects performance data anonymously and in aggregate (load times, routes visited). It does not use persistent cookies or identify individual users."
        ],
        section5Title: "Third-party cookies",
        section5Content: [
          "Some services integrated into our site may set their own cookies:",
          "<strong>Google reCAPTCHA v3:</strong> Used on registration forms to verify that the user is human. It sets the <strong>_GRECAPTCHA</strong> cookie under the <em>.google.com</em> domain (duration: session, up to 6 months). These cookies are managed by Google and are subject to their <a href='https://policies.google.com/privacy' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>privacy policy</a>.",
          "<strong>Supabase:</strong> Our database and authentication provider may generate technical identifiers necessary for server communication."
        ],
        section6Title: "Cookie management",
        section6Content: "You can configure your browser to <span>accept</span>, <span>block</span> or <span>delete</span> cookies at any time. You can also withdraw your consent by deleting the <strong>cookieConsent</strong> cookie from your browser, which will cause your preference to be requested again on your next visit. Please note that if you block some cookies, certain site functions may not be available. Below, we show you how to manage cookies in the most common browsers:",
        section6Links: [
          "<a href='https://support.google.com/chrome/answer/95647' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>Google Chrome</a>",
          "<a href='https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>Mozilla Firefox</a>",
          "<a href='https://support.apple.com/en-us/guide/safari/sfri11471/mac' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>Safari</a>",
          "<a href='https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09' target='_blank' rel='noopener noreferrer' class='text-blue-600 underline'>Microsoft Edge</a>"
        ],
        section7Title: "Updates to this policy",
        section7Content: "This cookie policy may be updated periodically to reflect changes in the services used or applicable regulations. We recommend reviewing it regularly. The date of the last update is indicated at the bottom of this page.",
        lastUpdated: "Last updated: March 2026",
      },
      avisoLegal: {
        title: "Legal Notice",
        subtitle: "Legal information about the use of this website and its contents",
        intro: "In compliance with Article 10 of Law 34/2002, of 11 July, on Information Society Services and Electronic Commerce (LSSI-CE), the following information about the owner of this website is made available to users.",
        section0Title: "Website owner information",
        section0: [
          "Name: EGS (incorporation in progress)",
          "Tax ID (NIF/CIF): pending registration",
          "Address: Santiago de Compostela, Galicia, Spain",
          "Email: info@egswater.com",
          "Website: https://egswater.com",
        ],
        section1Title: "Intellectual and industrial property",
        section1Content: "All contents, logos, images and texts published on this website are the property of <strong>EGS</strong> or are used under the appropriate licence. Their total or partial reproduction, distribution, transformation or public communication is prohibited without express written authorisation from EGS. Trademarks, trade names or distinctive signs are owned by EGS or by third parties who have authorised their use.",
        section2Title: "Liability",
        section2Content: "EGS is not responsible for errors, omissions or damages arising from the use of the information provided on this site or used for purposes other than those intended. EGS is likewise not responsible for the content of external websites linked from this portal, which are managed by their respective owners. EGS does not guarantee uninterrupted availability of the website and reserves the right to suspend or interrupt access to it for technical or maintenance reasons.",
        section3Title: "Applicable law and jurisdiction",
        section3Content: "These terms of use are governed by Spanish law, in particular the LSSI-CE and other applicable regulations. For the resolution of any dispute arising from the use of this website, the parties submit to the courts of Santiago de Compostela, expressly waiving any other jurisdiction that may apply.",
        section4Title: "Amendments to this legal notice",
        section4Content: "EGS reserves the right to modify this legal notice at any time. Changes will be published on this same page. We recommend reviewing this notice periodically to stay informed of any updates.",
        lastUpdated: "Last updated: March 2026",
      },
    },
    dashboard: {
      pageTitle: "Institutional Panel | EGS",
      session: {
        loading: "Loading",
        notStarted: "Session Not Started",
        notStartedDesc: "You are not logged in or your session has expired.",
        backToLogin: "Back to Login",
      },
      notifications: {
        types: {
          newFile: "New file uploaded",
          analysisComplete: "Analysis completed",
          filePersisted: "File persisted",
          analysisError: "Analysis error",
          readingsParsed: "Readings parsed",
          scoresCalculated: "Scores calculated",
          readingsError: "Readings parsing error",
          scoresError: "Scores error",
        },
        loading: "Loading...",
        empty: "No notifications",
      },
      menu: {
        resumen: "Summary",
        cargar: "Upload data",
        mapa: "Leak predictions",
        mapa2: "Excessive consumption",
        riesgos: "Risks",
        solicitudes: "Requests",
        incidencias: "Incidents",
        lecturas: "Readings",
        administracion: "Administration",
        informes: "Reports",
        configuracion: "Settings",
      },
      sidebar: {
        title: "Institutional Panel",
        logout: "Log Out",
        logoutDialogTitle: "Log Out",
        logoutDialogDesc: "Are you sure you want to log out?",
        logoutCancel: "Cancel",
        logoutConfirm: "Log Out",
      },
      severity: {
        ok: "OK",
        watch: "Watch",
        urgent: "Urgent inspection",
        estimated: "estimate",
      },
      sections: {
        riesgos: {
          title: "Risks",
          subtitle: "Leaks and excessive consumption by dwelling",
          tabLeaks: "Leaks",
          tabConsumption: "Excessive consumption",
          seccionLabel: "Section",
          periodoLabel: "Period",
          selectSeccion: "Select a section",
          selectPeriodo: "Select a period",
          mapTitle: "Risk map",
          togglePoints: "Points",
          toggleHeatmap: "Heatmap",
          panelTitle: "Dwelling detail",
          panelEmpty: "Select a dwelling on the map or table",
          impactLabel: "Estimated impact",
          searchHousing: "Search dwelling...",
          colHousing: "Dwelling",
          colPeriod: "Period",
          colProb: "Prob.",
          colImpact: "Impact",
          colSeverity: "Status",
          colCost: "Cost (est.)",
          pagPrev: "Previous",
          pagNext: "Next",
          pagShowing: "Showing {a}–{b} of {n}",
          noData: "No data available",
          selectToView: "Select a section and a period to view scores",
          totalAnalyzed: "Total dwellings analyzed:",
          legendOk: "OK (≤33%)",
          legendWatch: "Watch (34-66%)",
          legendUrgent: "Urgent inspection (>66%)",
          showing: "Showing:",
          createIncident: "Create incident",
          requestInspection: "Request inspection",
          toggleIncidents: "Incidents ({n})",
          mapSizeHint: "Marker size reflects expected cost; ①–⑤ mark the top priorities.",
          sortHint: "Sort by this column",
          sortedByCostNote: "Sorted by expected cost (probability × average consumption × tariff), highest first. Click a column to re-sort.",
          anomaliesTitle: "Detected anomalies",
          noAnomalies: "No anomalies in the readings",
          histTitle: "Reading history",
          seccionAll: "View all sections",
          loadingSections: "Loading section {done} of {total}...",
          colSeccion: "Section",
          legendSections: "Sections",
          sectionsWithData: "{n} of {m} sections with data in this period",
          breakdownTitle: "Breakdown by section",
          breakdownColSeccion: "Section",
          breakdownColViviendas: "Dwellings",
          breakdownColUrgentes: "Urgent",
          breakdownColImpacto: "Impact",
          breakdownColCoste: "Cost (est.)",
          breakdownHint: "Click a section to view it in detail.",
        },
        mapa: {
          title: "Leak Predictions",
          subtitle: "Detailed analysis of leak probabilities per dwelling",
          seccionLabel: "Section",
          periodoLabel: "Period",
          selectSeccion: "Select a section",
          selectPeriodo: "Select a period",
          probabilitiesSummary: "Probability Summary",
          low: "Low",
          medium: "Medium",
          high: "High",
          totalAnalyzed: "Total dwellings analyzed:",
          mapTitle: "Leak probability map",
          searchHousing: "Search dwelling...",
          colHousing: "Dwelling",
          colPeriod: "Period",
          colProb: "Prob.",
          noData: "No data available",
          selectToView: "Select a section and period to view scores",
          probabilityTitle: "Leak probability",
          tooltipPeriodo: "Period:",
          tooltipLeakProb: "Leak probability:",
          tooltipRisk: "Risk level:",
          legendLow: "Low probability (≤33%)",
          legendMedium: "Medium probability (34-66%)",
          legendHigh: "High probability (>66%)",
          showing: "Showing:",
        },
        mapa2: {
          title: "Excessive Consumption",
          subtitle: "Analysis of dwellings with abnormally high consumption",
          seccionLabel: "Section",
          periodoLabel: "Period",
          selectSeccion: "Select a section",
          selectPeriodo: "Select a period",
          searchHousing: "Search dwelling...",
          colHousing: "Dwelling",
          colPeriod: "Period",
          colProb: "Prob.",
          noData: "No data available",
          selectToView: "Select a section and period to view scores",
          probabilitiesSummary: "Excessive consumption summary",
          mapTitle: "Excessive consumption map",
          excessTitle: "Probability of excessive consumption",
          tooltipPeriodo: "Period:",
          tooltipExcessProb: "Probability of excessive consumption:",
          tooltipRisk: "Risk level:",
          low: "Low",
          medium: "Medium",
          high: "High",
          totalAnalyzed: "Total dwellings analyzed:",
          legendLow: "Low probability (≤33%)",
          legendMedium: "Medium probability (34-66%)",
          legendHigh: "High probability (>66%)",
          showing: "Showing:",
        },
        solicitudes: {
          title: "Requests",
          subtitle: "Monitor and manage your analysis requests",
          completed: "Completed:",
          inProgress: "In Progress:",
          pending: "Pending:",
          activeFilter: "Active Filter:",
          filterNone: "None",
          statusCompleted: "Completed",
          statusInProgress: "In Progress",
          statusPending: "Pending",
        },
        incidencias: {
          title: "Incidents",
          subtitle: "Incidents reported by the municipality's citizens",
          total: "Total:",
          pending: "Pending:",
          completed: "Completed:",
          noIncidents: "No incidents reported",
          pendingTitle: "Pending incidents",
          completedTitle: "Completed incidents",
          viewImages: "View images",
          markCompleted: "Mark as completed",
          markPending: "Mark as pending",
        },
        lecturas: {
          title: "Readings",
          subtitle: "Consumption reading records for all dwellings",
          searchPlaceholder: "Search by street, number, block, floor, door, section, date...",
          addBtn: "Add reading",
          modalTitle: "New reading",
          housingLabel: "Dwelling",
          housingPH: "Select a dwelling",
          dateLabel: "Reading date",
          counterLabel: "Meter reading (m³)",
          cancel: "Cancel",
          saving: "Saving...",
          save: "Save reading",
          deleteTitle: "Delete reading?",
          deleteDesc: "This action cannot be undone.",
          deleteCancelBtn: "Cancel",
          deleteConfirmBtn: "Delete",
          loadingText: "Loading readings...",
          noResults: "No results found",
          noReadings: "No readings recorded",
          colVia: "Street",
          colNum: "No.",
          colBloque: "Block",
          colPlanta: "Floor",
          colPuerta: "Door",
          colSeccion: "Section",
          colLectura: "Reading (m³)",
          colFecha: "Date",
          tabAnomalies: "Anomalies",
          tabAll: "All",
          anomalyZero: "Zero reading",
          anomalySpike: "Sudden spike",
          anomalyDrop: "Sudden drop",
          noAnomalies: "No anomalies detected in this set of readings",
        },
        administracion: {
          title: "Administration",
          subtitle: "Manage citizens and system users",
          searchCitizenPlaceholder: "Search citizen...",
          searchHousingPlaceholder: "Search dwelling...",
          colEmail: "Email",
          colName: "Name",
          colLastName: "Last Name",
          colPhone: "Phone",
          namePlaceholder: "e.g. John",
          lastNamePlaceholder: "e.g. Smith",
          emailPlaceholder: "e.g. user@example.com",
          dniPlaceholder: "e.g. 12345678A",
          phonePlaceholder: "e.g. 600123456",
          registerTitle: "Register new citizen",
          registerDesc: "Create an account for a citizen in your municipality",
          nombreLabel: "Name *",
          apellidosLabel: "Last Name *",
          emailLabel: "Email *",
          dniLabel: "ID *",
          telefonoLabel: "Phone (optional)",
          registering: "Registering...",
          registerBtn: "Register citizen",
          privacyNotice: "The data entered will be processed in accordance with our ",
          helpText: "An automatic password will be generated and an email with instructions will be sent to the user.",
          pendingUsersTitle: "Users pending validation",
          pendingUsersDesc: "Citizens who have requested access and are awaiting your approval",
          noPendingUsers: "No users pending validation",
          allUsersTitle: "All citizens",
          allUsersDesc: "Complete list of validated citizens in your municipality",
          noUsers: "No citizens registered",
        },
        configuracion: {
          title: "Settings",
          subtitle: "Manage your account and preferences",
          changePassword: "Change Password",
          newPasswordLabel: "New Password",
          confirmPasswordLabel: "Confirm New Password",
          minPasswordPlaceholder: "Minimum 6 characters",
          repeatPasswordPlaceholder: "Repeat password",
          updating: "Updating...",
          updatePassword: "Update Password",
          notificationsTitle: "Notifications",
          emailNotifications: "Email Notifications",
          emailNotificationsDesc: "Receive updates by email",
          saveButton: "Save settings",
          saving: "Saving...",
          saveNotificationsBtn: "Save preferences",
          accountTitle: "Account information",
          orgLabel: "Organization",
          emailLabel: "Email",
          roleLabel: "Role",
          roleInstitution: "Institution",
          roleCitizen: "Citizen",
          roleUnknown: "Unknown",
          tarifaTitle: "Water tariff",
          tarifaDesc: "Price per m³ used to estimate the economic impact of leaks and excessive consumption",
          tarifaLabel: "Price per m³ (€)",
          tarifaDecimalHint: "(use a comma for decimals, e.g. 1,80)",
          tarifaSaveBtn: "Save tariff",
          tarifaSaved: "Tariff saved",
        },
        cargar: {
          title: "Upload Data",
          subtitle: "Upload data files for analysis",
        },
        resumen: {
          title: "Summary",
          subtitle: "View dwellings with the highest and lowest scores",
          loading: "Loading scores...",
          excessTitle: "Highest probability of excessive consumption",
          leakTitle: "Highest leak probability",
          period: "Period:",
          noData: "No data available",
          seccionLabel: "Section",
          periodoLabel: "Period",
          pendingRequestsTitle: "Pending requests",
          pendingRequestsDesc: "Tracking of ongoing processes",
          kpiWaterAtRisk: "Water at risk",
          kpiHighRiskHomes: "High-risk dwellings",
          kpiOpenIncidents: "Open incidents",
          kpiConsumptionChange: "Consumption vs previous period",
          priorityTitle: "Today's priorities",
          prioritySubtitle: "Dwellings with the highest impact right now",
          priorityEmpty: "No priority dwellings in this period",
          mapTitle: "Risk map",
          kpiWaterAtRiskHint: "Sum of the estimated impact of the {n} analyzed dwellings (leaks + excessive consumption).",
        },
        informes: {
          title: "Reports",
          subtitle: "Generate printable reports from the dashboard data",
          seccionLabel: "Section",
          periodoLabel: "Period",
          selectSeccion: "Select a section",
          selectPeriodo: "Select a period",
          templateLabel: "Template",
          templateTecnico: "Technical risk report",
          templateTecnicoDesc: "KPIs, priority action points, map and anomaly appendix.",
          templateEficiencia: "Water efficiency report",
          templateEficienciaDesc: "Aimed at justification before Augas de Galicia and water efficiency grants.",
          generateBtn: "Generate report",
          selectToGenerate: "Select a section and a period to generate the report",
          generatingBtn: "Generating…",
          generatesInBackground: "The report is generated in the background and shows up in the table when it is done.",
          generatingHint: "You can keep using the panel; the report will show up in the table when it is done.",
          generatingToast: "Generating the report…",
          generatedOk: "Report generated and saved.",
          generateError: "The report could not be generated.",
          savedTitle: "Generated reports",
          savedSubtitle: "Every report is saved when generated. Open it to edit the texts or download the PDF.",
          colTitulo: "Report",
          colAmbito: "Scope",
          colPeriodo: "Period",
          colPlantilla: "Template",
          colCreado: "Generated",
          colAcciones: "Actions",
          ambitoConcello: "Whole municipality",
          openBtn: "Open",
          deleteBtn: "Delete",
          confirmDelete: "Delete this report? This cannot be undone.",
          deletedOk: "Report deleted.",
          deleteError: "The report could not be deleted.",
          listError: "Saved reports could not be loaded.",
          emptyList: "You haven't generated any report yet.",
          loadingList: "Loading reports…",
          refresh: "Refresh",
          prev: "Previous",
          next: "Next",
          pageOf: "of",
        },
      },
      informe: {
        downloadBtn: "Download PDF",
        generatingBtn: "Generating…",
        back: "Back to dashboard",
        loading: "Loading report data...",
        error: "Could not load the report data.",
        generatedOn: "Generated on",
        municipality: "Municipality",
        section: "Section",
        period: "Period",
        estimateDisclaimer: "The m³ and € figures are estimates (probability × average consumption × tariff) and do not replace a field inspection.",
        editableHint: "(click to edit)",
        techTitle: "Technical water risk report",
        summaryHeading: "Executive summary",
        summaryPlaceholder: "Write an executive summary of the period here: main findings, critical dwellings and recommended action.",
        seccionesHeading: "Sections by priority",
        allSecciones: "All sections",
        generateAI: "Generate with AI",
        generatingAI: "Generating…",
        aiError: "The summary could not be generated. Please try again.",
        aiSessionError: "Your session has expired. Sign in again to generate the summary.",
        aiDisclaimer: "Text generated automatically from the period's data. Review and edit it before publishing the report.",
        kpiHeading: "Period indicators",
        kpiWater: "Water at risk (estimated)",
        kpiHighRisk: "High-risk dwellings",
        kpiIncidents: "Open incidents",
        kpiTotal: "Dwellings analyzed",
        topHeading: "Priority action points (top 10)",
        colHousing: "Dwelling",
        colProb: "Prob.",
        colImpact: "Impact",
        colCost: "Cost (est.)",
        colStatus: "Status",
        mapHeading: "Risk map",
        anomaliesHeading: "Appendix: anomalies detected in the readings",
        anomalyColHousing: "Dwelling",
        anomalyColType: "Anomaly",
        anomalyColDate: "Date",
        noAnomalies: "No anomalies were detected in the period.",
        observationsHeading: "Technician's observations",
        observationsPlaceholder: "Note field observations, decisions made and next steps.",
        effTitle: "Water efficiency report",
        effSubtitle: "Supporting document for justification before Augas de Galicia and water efficiency grant applications",
        lossesHeading: "Estimated loss volume and associated cost",
        lossesDesc: "Aggregate estimate of water at risk from leaks and excessive consumption in the analyzed period.",
        statusBreakdownHeading: "Dwellings by status",
        prioritiesHeading: "List of priority action points",
        manualDataHeading: "Network data (manual entry)",
        injectedLabel: "Water injected into the network (m³)",
        anrLabel: "Non-revenue / unbilled water (m³)",
        iliLabel: "Loss index (ILI or other indicator)",
        manualHint: "These figures are not computed automatically yet; fill them in manually or check the project TODO.",
        actionsHeading: "Actions taken",
        actionsPlaceholder: "Describe the actions carried out: inspections, repairs, meter replacement, awareness campaigns...",
        measuresHeading: "Planned measures",
        measuresPlaceholder: "Describe the planned measures and the estimated schedule.",
        saveBtn: "Save changes",
        retrySave: "Retry save",
        saving: "Saving…",
        saved: "Saved",
        unsaved: "Unsaved changes",
        savedOk: "Report saved.",
        saveError: "The report could not be saved.",
      },
      upload: {
        title: "Upload Data File",
        subtitle: "Upload a file with the request data",
        fileLabel: "File",
        formats: "📁 Formats: CSV, XLSX.",
        uploadBtn: "Upload",
        selectFile: "Choose file",
        noFileSelected: "No file selected",
        privacyNotice: "By uploading this file you confirm its processing complies with our ",
        privacyLink: "Privacy Policy",
      },
      imageModal: {
        title: "Incident Images",
      },
      messages: {
        incidentCompleteError: "Error marking incident as completed. Please try again.",
        incidentPendingError: "Error marking incident as pending. Please try again.",
        userRegistered: "User registered successfully. An email with instructions has been sent.",
        userDeleted: "User deleted successfully",
        userDeleteError: "Error deleting user",
        confirmDeleteUserPrefix: "Are you sure you want to permanently delete user",
        confirmDeleteUserSuffix: "?\n\nThis action cannot be undone and will delete all their data.",
        confirmIncidentPending: "Are you sure you want to mark this incident as pending?",
        addressUnavailable: "Address unavailable",
      },
    },
    ciudadano: {
      pageTitle: "Personal Panel | EGS",
      privacyFooter: "The data you enter in this application is processed in accordance with our ",
      session: {
        loading: "Loading",
        notStarted: "Session Not Started",
        notStartedDesc: "You are not logged in or your session has expired.",
        backToLogin: "Back to Login",
      },
      menu: {
        inicio: "Home",
        addVivienda: "Add Dwelling",
        consultarViviendas: "View Dwellings",
        addLectura: "Add Reading",
        consultarLecturas: "View Readings",
        addIncidencia: "Add Incident",
        consultarIncidencias: "View Incidents",
        configuracion: "Settings",
      },
      sidebar: {
        logout: "Log Out",
        logoutDialogTitle: "Log Out",
        logoutDialogDesc: "Are you sure you want to log out?",
        logoutCancel: "Cancel",
        logoutConfirm: "Log Out",
      },
      validation: {
        fillAllFields: "❌ Please fill in all fields",
        passwordNoMatch: "❌ Passwords do not match",
        passwordTooShort: "❌ Password must be at least 6 characters",
        passwordUpdated: "✅ Password updated. Redirecting to login...",
        serverError: "The server did not return a valid response",
      },
      messages: {
        incidentDeleteError: "Error deleting incident. Please try again.",
        notifLoadError: "Could not load notification preferences",
        notifSaveError: "Could not save preferences",
        unknownMunicipality: "Unknown municipality",
      },
      sections: {
        inicio: {
          title: "Home",
          welcome: "Welcome, ",
          addHousingTitle: "Add dwelling",
          addHousingDesc: "Register a new dwelling",
          viewHousingTitle: "View dwellings",
          addReadingTitle: "Add reading",
          addReadingDesc: "Record meter readings",
        },
        addVivienda: {
          title: "Add Dwelling",
          subtitle: "Register a new dwelling in your account",
          formTitle: "New dwelling",
          viaLabel: "Street",
          viaPH: "Select a street",
          numeroLabel: "Number",
          numeroPH: "Select a number",
          noNumero: "No number",
          bloqueLabel: "Block",
          bloquePH: "Select a block",
          noBloque: "No block",
          plantaLabel: "Floor",
          plantaPH: "Select a floor",
          noPlanta: "No floor",
          puertaLabel: "Door",
          puertaPH: "Select a door",
          noPuerta: "No door",
          cancel: "Cancel",
          save: "Save dwelling",
          cusecInfoBefore: "ℹ️ The CUSEC code will be automatically assigned as",
          cusecDesc: "(Concello de Cerdedo)",
        },
        consultarViviendas: {
          title: "View Dwellings",
          subtitle: "Registered dwellings",
          tableTitle: "Registered dwellings",
          searchPlaceholder: "Search dwelling...",
          noHousing: "No dwellings registered",
          addFirstHousing: "Add first dwelling",
          noResults: "No dwellings found matching the search",
          colVivienda: "Dwelling",
          colPeriod: "Period",
          colType: "Type",
          colProb: "Prob.",
          colRiesgo: "Risk",
          colAcciones: "Actions",
          leakProb: "Leak probability",
          excessConsumption: "Excessive consumption",
          noData: "No data",
          pending: "⏳ Pending",
          deleteHousing: "Delete dwelling",
          period: "Period:",
          scoreLabel: "Score:",
          riskLabel: "Risk:",
          riskLow: "Low",
          riskMedium: "Medium",
          riskHigh: "High",
        },
        addLectura: {
          title: "Add Reading",
          subtitle: "Record a new meter reading",
          formTitle: "New reading",
          noHousing: "No dwellings registered",
          addHousingFirst: "Add dwelling first",
          housingLabel: "Dwelling",
          housingPH: "Select a dwelling",
          dateLabel: "Reading date",
          counterLabel: "Meter reading",
          cancel: "Cancel",
          save: "Save reading",
        },
        consultarLecturas: {
          title: "View Readings",
          subtitle: "Registered readings",
          tableTitle: "Registered readings",
          searchPlaceholder: "Search by dwelling...",
          noReadings: "No readings recorded",
          addReading: "Add reading",
          noResults: "No readings found matching the search",
          colVivienda: "Dwelling",
          colFecha: "Date",
          colLectura: "Reading",
          viviendaLabel: "Dwelling:",
          fechaLabel: "Date:",
          lecturaLabel: "Reading:",
        },
        addIncidencia: {
          title: "Add Incident",
          subtitle: "Report a new incident",
          formTitle: "New incident",
          noHousing: "No dwellings registered",
          addHousingFirst: "Add dwelling first",
          housingLabel: "Dwelling",
          housingPH: "Select a dwelling",
          subjectLabel: "Subject",
          subjectPH: "Write a brief summary",
          descLabel: "Description",
          descPH: "You can elaborate here",
          imagesLabel: "Images (optional, maximum 2)",
          addImage: "Take photo or select image",
          addAnotherImage: "Add another image",
          cancel: "Cancel",
          save: "Save incident",
        },
        consultarIncidencias: {
          title: "View Incidents",
          subtitle: "Saved incidents",
          total: "Total:",
          pending: "Pending:",
          completed: "Completed:",
          noIncidents: "No incidents saved",
          noIncidentsDesc: "Start by reporting your first incident",
          addIncident: "Add incident",
          pendingTitle: "Pending incidents",
          completedTitle: "Completed incidents",
          subjectLabel: "Subject",
          descLabel: "Description",
          viewImages: "View images",
          pendingBadge: "Pending",
          completedBadge: "Completed",
        },
        configuracion: {
          title: "Settings",
          subtitle: "Manage your account and preferences",
          changePassword: "Change Password",
          newPasswordLabel: "New Password",
          confirmPasswordLabel: "Confirm New Password",
          minPasswordPlaceholder: "Minimum 6 characters",
          repeatPasswordPlaceholder: "Repeat password",
          updating: "Updating...",
          updatePassword: "Update Password",
          notificationsTitle: "Email Notifications",
          notificationsDesc: "Receive updates by email",
          saveNotifications: "Save",
          notificationsHeader: "Notifications",
          saving: "Saving...",
          saveNotificationsBtn: "Save preferences",
          accountTitle: "Account information",
          orgLabel: "Organization",
          emailLabel: "Email",
          roleLabel: "Role",
        },
      },
    },
  },
} as const;

export function getTranslation(lang: Language) {
  return translations[lang];
}
